// this should move to the DB (watch out for the todo:db comments first, they should be pretty quick to del with before proceeding)

import { ElementType, IQuestionConfig } from 'src/app/ui-testrunner/models';
import { TestFormConstructionMethod } from '../../item-set-editor/models/assessment-framework';
import { AuditResultType, IAuditResultSummary } from '../../item-set-editor/controllers/audits';
import { ECheckFunctionIds, ISpecialParams } from './checks';
import { InputFormat } from 'src/app/ui-testrunner/element-render-input/model';
import { scoreMatrixElementTypes } from '../../config-score-matrix/models';
import { McqDisplay } from 'src/app/ui-testrunner/element-render-mcq/model';
import { TextParagraphStyle } from 'src/app/ui-testrunner/element-render-text/model';

export interface IAuditConfig {
    auditType: AuditType,
    auditTarget?: AuditTarget, 
    slug: string,
    caption: {en: string, fr: string},
    itemScope?: AuditQuestionScope[],
    checks?: TAuditCheck[],
    commonCheckProps?: Partial<TAuditCheck>,
    defaultScope?: string,
    isCompleteCheck?:boolean, 
    isCompleteFix?:boolean, 
    isCustomResult?:boolean, 
    isContentDiff?:boolean, 
    autoFixes?: IAuditPatch, 
    isDisabled?:boolean, 
    isHidden?:boolean, // for work in progress
    isLangSensitive?: boolean, // controls wether or not we want to retrieve questions relative to curr lang
    isAsync?: boolean, // if the audit has checks that require async operations
    excludedFrameworkFlags?: AuditAssesmentType[];
    requiredFrameworkFlags?: AuditAssesmentType[]; // Exclude if framework is a certain
    requiredFrameworkParamCodes?: string[]; // Exclude if framework is a certain
}
export type TAuditCheck = (IAuditCheckByBlockType | IAuditCheckByContainsBlockType | IAuditCheckByPropConfig | IAuditCheckByPropVal | IAuditCheckByFunctions | IAuditCheckComposite)
export type AuditMetaRecord = Record<AuditType, IAuditResultSummary>;
export type AuditAssesmentType = 
    TestFormConstructionMethod 
    | AuditAssesmentTypeFlags


export enum AuditAssesmentTypeFlags {
    QUESTIONNAIRE = "QUESTIONNAIRE",
    TESTLETS = "TESTLETS",
    PRESERVED_PARAMS = "PRESERVED_PARAMS",
    ALTERNATIVE = "ALTERNATIVE",
    PT = "PT",
    IS_RESOURCE_ONLY = "IS_RESOURCE_ONLY"
}

/**
 * The content that we are auditing (e.g. forms, panels, questions (default))
 * It'll dictate what is store in auditResults items
 */
export enum AuditTarget {
    QUESTIONS = "QUESTIONS",
    MSCAT_PANELS = "MSCAT_PANELS",
    TLOFT_PANELS = "TLOFT_PANELS",
    LINEAR_SECTIONS = "LINEAR_SECTIONS",
    PANELS = "PANELS" // FOR MSCAT/TLOFT/LINEAR
}

export const PANEL_AUDIT_TYPE_TO_TARGET_DICT: Partial<Record<TestFormConstructionMethod, AuditPanelTargets>> = {
    [TestFormConstructionMethod.TLOFT]: AuditTarget.TLOFT_PANELS,
    [TestFormConstructionMethod.MSCAT]: AuditTarget.MSCAT_PANELS,
    [TestFormConstructionMethod.LINEAR]: AuditTarget.LINEAR_SECTIONS,
};

export enum AuditLevel {
    ITEM = "ITEM",
    FRAMEWORK = "FRAMEWORK"
}

export interface IQuestionScoreCategoryEntry {
  score: number;
  scoreCategory: number;
  question_id: string;
}
export type IQuestionScoreCategory = IQuestionScoreCategoryEntry[]
/** General grouping for audits */

export type AuditPanelTargets = AuditTarget.MSCAT_PANELS | AuditTarget.TLOFT_PANELS | AuditTarget.LINEAR_SECTIONS;

export const TestletRequiredTargets = new Set([AuditTarget.MSCAT_PANELS, AuditTarget.TLOFT_PANELS])

export type AuditType =
    "DATA"
    | "CONTENT"
    | "PUBLISH" // currently for question diff audits that check between
    | "ADIF"
    | "PSYCH" // audits that are only relevant for the psych team


export enum AuditCheckType {
    PROP_VAL = 'PROP_VAL', // to check a single question param
    BY_BLOCK_TYPE =  'BY_BLOCK_TYPE', // to check against questions containing specific block types (deep search)
    ITEM_SETTINGS = 'ITEM_SETTINGS', // to check item settings or anything that is emmbedded directly in the qconfig 
    PROPS_CONFIG = 'PROPS_CONFIG', // to check combination of params if all the checks are true it'll flag the item
    PROPS_CONFIG_PSYCH_STATS = 'PROPS_CONFIG_PSYCH_STATS',
    PROPS_CONFIG_PSYCH_STATS_OR = 'PROPS_CONFIG_PSYCH_STATS_OR',
    PROPS_CONFIG_OR = 'PROPS_CONFIG_OR', // to check if any single condition is true
    BY_CONTAINS_BLOCK_TYPE = 'BY_CONTAINS_BLOCK_TYPE',
    BY_FUNCTION = 'BY_FUNCTION',
    COMPOSITE = 'COMPOSITE',
    COMPOSITE_OR = 'COMPOSITE_OR',
    NONE = "NONE" // overwrite common check for custom logic
}
export interface IAuditPatch {
    slug: string,
    checkCompositeSlug?: string,
    caption: {en:string, fr:string},
    checks?: {id: string},
    fixType?: AuditCheckType,
    fixConfigs?: IFixByBlockType[],
}

export interface IAuditCheck {
    id: string,
    caption?:  {en: string, fr: string},
    typeCaption?: string,
    checkElType?:EAuditElementType,
    isInverted?:boolean,
    isDisabled?:boolean,
    excludedItems?: SubAuditExcludedItems[], // used to exclude items from sub audits
    informativeOnly?: InformativeCheckType, // used to exclude subaudits from issue count (doesn't apply to custom results)
    autoFix?: boolean; // used for non custom result audits to determine if they are auto fix
    isHidden?: boolean; // hidden due to filtering client only
    requiredFrameworkFlags?: AuditAssesmentType[]; // Exclude if framework is a certain
    excludedFrameworkFlags?: AuditAssesmentType[]; // Exclude if framework is a certain
    requiredFrameworkParamCodes?: string[]; // Exclude if framework is a certain

}

export interface IAuditCheckByBlockType extends IAuditCheck {
    checkType?: AuditCheckType.BY_BLOCK_TYPE;
    checkConfig?: ICheckByBlockType;
}

export interface IAuditCheckByPropVal extends IAuditCheck {
    checkType?: AuditCheckType.PROP_VAL | AuditCheckType.ITEM_SETTINGS;
    checkConfig?: ICheckPropVal;
}

export interface IAuditCheckByPropConfig extends IAuditCheck {
    checkType?: TPropConfigChecks;
    checkConfig?: ICheckProp[];
}

export interface IAuditCheckByContainsBlockType extends IAuditCheck {
    checkType?: AuditCheckType.BY_CONTAINS_BLOCK_TYPE;
    checkConfig?: ICheckByContainsBlockType;
}

export interface IAuditCheckByFunctions extends IAuditCheck {
    checkType?: AuditCheckType.BY_FUNCTION;
    checkConfig?: ICheckByFunction 
}

export interface IAuditCheckComposite extends IAuditCheck {
    checkType?: AuditCheckType.COMPOSITE | AuditCheckType.COMPOSITE_OR;
    checkConfig?: Partial<TAuditCheck>[] 
}

export type TPropConfigChecks = AuditCheckType.PROPS_CONFIG | AuditCheckType.PROPS_CONFIG_OR | AuditCheckType.PROPS_CONFIG_PSYCH_STATS | AuditCheckType.PROPS_CONFIG_PSYCH_STATS_OR

export const ParamCheckTypes:AuditCheckType[] = [AuditCheckType.PROPS_CONFIG, AuditCheckType.PROP_VAL, AuditCheckType.PROPS_CONFIG_OR];

export type InformativeCheckType = // this is done to allow for more complex checks of if the audit is informative only
    boolean 
    | 'SAMPLE_TEST'
    | 'NOT_SAMPLE_TEST'
    | 'IS_NOT_ALTERNATIVE';

export interface ICheckProp {
    targetProp: string;
    filterMethod: EFilterMethods;
    subFilterMethod?: EFilterMethods;
    filterValue?: string | number | boolean | any[];
    }
export interface ICheckPropVal {
    targetProp?: string,
    isScoringConfig?: boolean,
    filterMethod?: EFilterMethods, // condition on which the check with fail in this case
    filterValue?: (string | number | boolean)[] | string | number | boolean,
    transforms?: {
        type: BlockPropValTransformTypes,
        config?: any,
    }[]
}

type CheckConfig<K extends ECheckFunctionIds> = K extends keyof ISpecialParams
    ? {
        functionId: K;
        // If params exist in map, they are REQUIRED here
        functionParams: ISpecialParams[K];
    }
    : {
        functionId: K;
        functionParams?: any;
    };
export type ICheckByFunction = {
    [K in ECheckFunctionIds]: CheckConfig<K>
}[ECheckFunctionIds];

export interface ICheckByBlockType {
    targetBlockType?: string,
    relativePropPath?: string,
    filterMethod?: EFilterMethods,
    filterValue?: string | number | boolean,
    transforms?: {
        type: BlockPropValTransformTypes,
        config?: ITransformConfig,
    }[]
    isMissing?: boolean,
    isArrMissingOrEmpty?: boolean,
    dndKeys?: DndCheckFlags[],
    setHas?: string[],
    setMissing?: string[],
    setHasAll?: string[]
    // isMissing: boolean,
}

export interface ICheckByContainsBlockType {
    targetElementGrouping?: TargetElementGrouping
}
export enum TargetElementGrouping {
    RESPONSE_TYPE_BLOCK_MARKED_AS_READING = "RESPONSE_TYPE_BLOCK_MARKED_AS_READING"
}

export interface ITransformConfig {
    conditions:{propPath:string, val:any, method:EFilterMethods}[]
}

export interface IFixByBlockType {
    targetBlockType?: ElementType,
    propPath: string,
    val: any,
}

export type CheckId = string;

export enum EItemTypesParam {
  DDP = 'DDP',
  DP = 'DP',
  CLK = 'CLK',
  DG = 'DG',
  CH = 'CH',
  OR = 'OR',
  LW = 'LW',
  SW = 'SW'
}

export enum ETEITypesParam {
  CLICK_RESPONSE = 'Click Response',
  DRAG_AND_DROP = 'Drag and Drop',
  DROP_DOWN = 'Drop Down',
  CHECKLIST = 'Checklist',
  MULTIPLE_SELECT = 'Multiple Select'
}


export const ITEM_MAP_MODULE_META_AUDIT_CONST_LABELS = ['MM_LW', 'MM_WMC'];

export type BlockPropValTransformTypes = 'URL_TO_TYPES' 
    | 'ARR_FILTER'
    | ''
    | '';

export type DndCheckFlags = 'hasDuplicateKeyIds'
    | 'hasMissingKeyIds'
    | 'hasMissingHomeTargetIds'
    | 'hasDuplicateHomeTargetIds'
    '';

export enum EFilterMethods {
  NOT_EQUAL = 'NOT_EQUAL',
  EQUAL = 'EQUAL',
  IS_FALSEY = 'IS_FALSEY',
  IS_TRUTHY = 'IS_TRUTHY',
  IS_NULL = "IS_NULL",
  IS_NOT_NULL = 'IS_NOT_NULL',
  IS_ARR_EMPTY = 'IS_ARR_EMPTY',
  GREATER_THAN = 'GREATER_THAN',
  LESS_THAN = 'LESS_THAN',
  IS_NAN = 'IS_NAN', // check if it's not a number 
  IS_NEGATIVE = 'IS_NEGATIVE', // checks if value is negative or not a number
  DOES_NOT_INCLUDE = 'DOES_NOT_INCLUDE', // check if value isn't in an array
  INCLUDES = 'INCLUDES',
  TARGET_DOES_NOT_INCLUDE = 'TARGET_DOES_NOT_INCLUDE', // check if target does not include value in array/string
  TARGET_INCLUDES = 'TARGET_INCLUDES', // check if target includes value in array/string
  NUMBER_MISCONFIGURED = 'NUMBER_MISCONFIGURED',
  DECIMAL_ROUNDING = 'DECIMAL_ROUNDING',
  ARRAY_LENGTH_COMPARISON_EQUAL = 'ARRAY_LENGTH_COMPARISON_EQUAL',
  ARRAY_LENGTH_COMPARISON_GREATER = 'ARRAY_LENGTH_COMPARISON_GREATER',
  ARRAY_LENGTH_COMPARISON_LESSER = 'ARRAY_LENGTH_COMPARISON_LESSER',
  STRING_INCLUDES_VALUES = 'STRING_INCLUDES_VALUES',
  STRING_INCLUDES_SPECIAL_CHAR = 'STRING_INCLUDES_SPECIAL_CHAR',
  EMPTY = '' // keep the empty string option
}


export type EAuditElementType = 'ASSET' | 'QUESTION';

//? All scopes are subset of SCREENS except ITEM_BANK
export enum AuditQuestionScope {
    ITEMS_SCORED = 'ITEMS_SCORED', // fetches all the questions that are not reading selection pages and not questionnaires
    ITEMS_SCORED_MINUS_FT = 'ITEMS_SCORED_MINUS_FT', // fetches all scored questions which do not have a field trial flag set in their meta data
    ITEMS_HUMAN_SCORED = 'ITEMS_HUMAN_SCORED', // fetches all questions that look like they should be human scored
    ITEMS_SCORED_MINUS_HS = 'ITEMS_SCORED_MINUS_HS', // fetches all scored questions that are not human scored
    ITEMS_SURVEY = 'ITEMS_SURVEY', // fetches all questions that are questionnaires, or if the assessment framework in context is a questionnaire assessment, it fetches all questions
    SCREENS = 'SCREENS', // fetches all the screens (question being used) (which includes items)
    SCREENS_NONSCORED = 'SCREENS_NONSCORED', // todo
    SCREENS_PASSAGES = 'SCREENS_PASSAGES', // fetches all screens that are reading selection pages
    SCREENS_NON_PASSAGES = 'SCREENS_NON_PASSAGES', // fetches all the questions that are not reading selection pages
    ITEM_BANK = 'ITEM_BANK', // fetches all questions in item bank
    ITEM_BANK_SCORED = 'ITEM_BANK_SCORED', // fetches all questions in item bank that are scored
    SCREENS_INCLUDE_DISABLED = 'SCREENS_INCLUDE_DISABLED', // extra flag to see which 
    PASSAGE_ITEMS = 'PASSAGE_ITEMS', // to retrieve all questions in testlets/sections that have a reading passage or have reading selection config filled
    ITEM_BANK_PASSAGES = 'ITEM_BANK_PASSAGES',
    SCREENS_PASSAGES_MAP_MODULE = 'SCREENS_PASSAGES_MAP_MODULE',
    NONE = 'NONE' // To not retrieve any questions primarily used for when we want to provide the question array
  }
  
export type SubAuditExcludedItems =
      'QUESTIONNAIRE'
    | 'READING_SELECTIONS'
    | 'FT' // items with ft set to true
    | 'NON_FT'
    | 'WRITING_WITHOUT_ASSOCIATED_RESOURCE'
    | 'NON_READING_SELECTIONS'
    | 'NO_RESOURCE_OR_SELECTION'
    | 'ALTERNATIVE'
    | 'NON_ALTERNATIVE'
    | 'HUMAN_SCORED'
    | 'NON_HUMAN_SCORED'
;

export interface IAuditFilterScopedQuestions {
    [scopeSlug: string /* AuditQuestionScope */ ]: boolean,
}

export const AuditScopes:IAuditFilterScopedQuestions[] = [
    {}
]
export const AuditConfigs___LEGACY:IAuditConfig[] = [
    {
        auditType: 'CONTENT',
        slug: 'QUESTION_UTIL',
        caption: {en: 'Overall Question Utilization', fr: 'Overall Question Utilization'},
        itemScope: [AuditQuestionScope.ITEM_BANK],
        requiredFrameworkFlags: [AuditAssesmentTypeFlags.TESTLETS],
        isCompleteCheck: true,
        commonCheckProps: {
            informativeOnly: true
        },
        checks: [
            {
                id: 'UTIL_TESTLET',
                caption: {en: 'do not appear in any testlets', fr: 'do not appear in any testlets'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.UTIL_TESTLET
                }
            },
            {
                id: 'UTIL_QUAD',
                caption: {en: 'do not appear in any quadrants', fr: 'do not appear in any quadrants'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.UTIL_QUAD
                }
            },
        ],
        // autoFixes: [
        //     { 
        //         slug:'xxxxx',
        //         caption: {en: '', fr: ''},
        //     }, 
        // ],
    },
    {
        auditType: 'CONTENT',
        slug: 'QUESTION_LABEL',
        caption: {en: 'Duplicate Item Label', fr: 'Duplicate Item Label'},
        itemScope: [AuditQuestionScope.SCREENS, AuditQuestionScope.ITEM_BANK],
        isCompleteCheck: true,
        checks: [
            {
                id: 'Q_LABEL_DUPE',
                caption: {en: 'items with overlapping labels', fr: 'items with overlapping labels'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.Q_LABEL_DUPE
                }
            },
        ],
        // autoFixes: [
        //     { 
        //         slug:'xxxxx',
        //         caption: {en: '', fr: ''},
        //     }, 
        // ],
    },
    {
        auditType: 'CONTENT',
        slug: 'CLONES',
        caption: {en: 'Clones', fr: 'Clones'},
        itemScope: [AuditQuestionScope.ITEMS_SCORED],
        commonCheckProps: {
            informativeOnly: true
        },
        isCompleteCheck: true,
        autoFixes: { // N_CLONES_ISSUES
            slug: 'CLONES_IS_AUTOFIXING',
            caption: {en: '', fr: ''},
        },
        checks: [
            {
                id: 'Q_CLONES_MISSING_PARAM', caption: {en: 'Belong to a clone group but do not have the indicated parameter', fr: 'Belong to a clone group but do not have the indicated parameter'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.Q_CLONES_MISSING_PARAM
                }
            },
            {
                id: 'Q_CLONES_INVALID_PARAM', caption: {en: 'Belong to a clone group but may not be properly identified/categorized', fr: 'Belong to a clone group but may not be properly identified/categorized'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.Q_CLONES_INVALID_PARAM
                }
            }
        ]
    },
    {
        auditType: 'CONTENT',
        slug: 'ENTRY_ID',
        caption: {en: 'Entry IDs', fr: 'Entry IDs'},
        itemScope: [AuditQuestionScope.SCREENS_NON_PASSAGES],
        isCompleteCheck: true,
        commonCheckProps: {
            informativeOnly: true
        },
        checks: [
            {
                id: 'UNTRACKED_ENTRIES', caption: {en: 'contain untracked entries', fr: 'contain untracked entries'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.UNTRACKED_ENTRIES
                }
            },
        ]

    },
    {
        auditType: 'CONTENT',
        slug: 'VOICEOVER',
        caption: {en: 'Voice-Over', fr: 'Voice-Over'},
        itemScope: [AuditQuestionScope.SCREENS],
        isCompleteCheck: true,
        checks: [
            {
                id: 'MISSING_OVERALL_VOICE', caption: {en: "Missing overall voice-over", fr: "Missing overall voice-over"}, autoFix: true, checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.MISSING_OVERALL_VOICE
                }
            },
            {
                id: 'MISSING_OPTION_VOICE', caption: {en: "Missing  option voice-over", fr: "Missing  option voice-over"}, autoFix: true, checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.MISSING_OPTION_VOICE
                }
            },
            {
                id: 'CONTAIN_DUPLICATED_OPTION_VOICE', caption: {en: "Contains duplicated option voice", fr: "Contains duplicated option voice"}, checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.CONTAIN_DUPLICATED_OPTION_VOICE
                }
            },
        ],
        autoFixes: {
            slug: 'VOICEOVER_IS_AUTOFIXING',
            caption: {en: 'Apply Auto-Fixes', fr: 'Apply Auto-Fixes'},
        },
    },
    // {
    //     auditType: 'CONTENT',
    //     slug: 'IP_RIGHTS',
    //     caption: {en: 'Intellectual Property', fr: 'Intellectual Property'},
    //     itemScope: [AuditQuestionScope.SCREENS],
    //     isAsync: true,
    //     checks: [
    //         {id:'NOT_COMPLETED', caption: {en: 'not marked as Completed', fr: 'not marked as Completed'}, checkElType:'ASSET',},
    //         {id:'NOT_INCLUDED_EN', caption: {en: 'contain assets not included in the Asset Library (en)', fr: 'contain assets not included in the Asset Library (en)'}, },
    //         {id:'NOT_INCLUDED_FR', caption: {en: 'contain assets not included in the Asset Library (fr)', fr: 'contain assets not included in the Asset Library (fr)'},},
    //         {id:'ALLOWED_IMPRESSIONS_NUM', caption: {en: 'with allowed impressions less than the number of test-takers', fr: 'with allowed impressions less than the number of test-takers'}, checkElType:'ASSET', },
    //     ],
    //     isDisabled: false,
    // },
    {
        auditType: 'CONTENT',
        slug: 'ACCESSIBILITY',
        itemScope: [AuditQuestionScope.SCREENS],
        isCompleteCheck: true,
        caption: {en: 'Accessibility', fr: 'Accessibility'},
        commonCheckProps: {
            informativeOnly: 'IS_NOT_ALTERNATIVE',
        },
        checks: [
            {
                id: 'IMAGES_MISSING_ALT_TEXT', caption: {en: 'images are missing alt-text', fr: 'images are missing alt-text'}, checkType: AuditCheckType.BY_FUNCTION, checkConfig: {
                    functionId: ECheckFunctionIds.IMAGES_MISSING_ALT_TEXT
                }
            },
            {
                id: 'NONE_CLK_ITEMS', caption: {en: 'Items that contain respondables that aren\'t MCQ or Keyboard Input', fr: 'Items that contain respondables that aren\'t MCQ or Keyboard Input'}, informativeOnly: 'IS_NOT_ALTERNATIVE', checkType: AuditCheckType.BY_FUNCTION, checkConfig: {
                    functionId: ECheckFunctionIds.CONTAINS_NOT_ALLOWED_RESPONSE_BLOCKS,
                    functionParams: {
                        allowedRespondables: [
                            ElementType.MCQ,
                            ElementType.INPUT
                        ]
                    }
                }
            }
        ],
    },
    {
        auditType: 'CONTENT',
        slug: 'PRINT_VERSION',
        caption: {en: 'Print Version', fr: 'Print Version'},
        itemScope: [AuditQuestionScope.SCREENS],
        checks: [
            { id: 'PRINT_FRIENDLY', typeCaption: 'pages', caption: {en: 'are not print-friendly', fr: 'are not print-friendly'} },
        ],
        isDisabled: true,
        isHidden: true,
    },
    {
        auditType: 'CONTENT',
        slug: 'MOBILE',
        caption: {en: 'Compatibility with Mobile', fr: 'Compatibility with Mobile'},
        itemScope: [AuditQuestionScope.SCREENS],
        isDisabled: true,
        isHidden: true,
    },
    {
        auditType: 'CONTENT',
        slug: 'BROWSERLOCK',
        caption: {en: 'Compatibility with Browser Lock', fr: 'Compatibility with Browser Lock'},
        itemScope: [AuditQuestionScope.SCREENS],
        isDisabled: true,
        isHidden: true,
    },
    {
        auditType: 'CONTENT',
        slug: 'COMMENTS',
        caption: {en: 'Comments', fr: 'Comments'},
        itemScope: [AuditQuestionScope.SCREENS],
        isAsync: true,
        isCustomResult: true, // this is actually custom results
        isCompleteCheck: true,
        checks: [
            {
                id: 'QS_W_OUTSTANDING_CMTS', caption: {en: 'Items with outstanding comments ', fr: 'Items with outstanding comments '}, informativeOnly: true, checkType: AuditCheckType.BY_FUNCTION, checkConfig: {
                    functionId: ECheckFunctionIds.QS_W_OUTSTANDING_CMTS
                }
            },
            {
                id: 'ASSETS_W_OUTSTANDING_CMTS', caption: {en: 'Assets with outstanding comments ', fr: 'Assets with outstanding comments '}, checkElType: 'ASSET', informativeOnly: true, checkType: AuditCheckType.BY_FUNCTION, checkConfig: {
                    functionId: ECheckFunctionIds.ASSETS_W_OUTSTANDING_CMTS
                }
            },
        ],
    },
    {
        auditType: 'CONTENT',
        slug: 'ASSESSMENT_KEYS',
        caption: {en: 'Assessment Keys', fr: 'Assessment Keys'},
        itemScope: [AuditQuestionScope.ITEMS_SCORED],
        isDisabled: true,
        isHidden: true,
    },
    // {
    //     auditType: 'CONTENT',
    //     slug: 'COMMON_ITEMS',
    //     caption: {en: 'Common Items', fr: 'Common Items'},
    //     itemScope: [AuditQuestionScope.ITEMS_SCORED],
    //     isAsync: true,
    //     checks: [
    //         {id:'COMMON_ITEMS', caption: {en: 'Common Items ', fr: 'Common Items '}, },
    //     ],
    // },
    {
        auditType: 'CONTENT',
        slug: 'TRAX_PARAM',
        caption: {en: 'TRAX Assessment Key', fr: 'TRAX Assessment Key'},
        itemScope: [AuditQuestionScope.ITEMS_SCORED],
        isDisabled: true,
        isHidden: true,
        checks: [
            { id: 'ASMT_SESS_NUM', caption: {en: 'have a missing or invalid ASSMT_SESSION key (6 numbers)', fr: 'have a missing or invalid ASSMT_SESSION key (6 numbers)'}, },
            { id: 'ASMT_CODE', caption: {en: 'have a missing or invalid ASSMT_CODE key (3 letters, 2 numbers)', fr: 'have a missing or invalid ASSMT_CODE key (3 letters, 2 numbers)'}, },
            { id: 'ASMT_MARK', caption: {en: 'have a missing or invalid MARK_VALUE key (1 or 2 digits)', fr: 'have a missing or invalid MARK_VALUE key (1 or 2 digits)'}, },
            { id: 'ASMT_SCALE', caption: {en: 'have a missing or invalid SCALE_FACTOR key (less than 9 digits)', fr: 'have a missing or invalid SCALE_FACTOR key (less than 9 digits)'}, },
            { id: 'ASMT_SESS_CHAR', caption: {en: 'have a missing or invalid ASSMT_SECTION key (starts with A or B)', fr: 'have a missing or invalid ASSMT_SECTION key (starts with A or B)'}, },
        ],
    },
    {
        auditType: 'PUBLISH',
        slug: 'CONTENT_DIFF_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS],
        caption: {en: 'Compare question config to published config', fr: 'Compare question config to published config'},
        isCustomResult: true,
        isContentDiff: true,
        isCompleteCheck: true,
        isAsync: true,
        checks: [
            {
                id: 'CURRENT_CONTENT_DIFF_AUDIT', caption: {en: 'Compare published question configs to current config', fr: 'Compare published question configs to current config'}, informativeOnly: true, checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.CURRENT_CONTENT_DIFF_AUDIT
                }
            },
        ],
    },
    {
        auditType: 'PUBLISH',
        slug: 'PUBLISHED_CONTENT_DIFF_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS],
        caption: {en: 'Compare Published Question Configs', fr: 'Compare Published Question Configs'},
        isCustomResult: true,
        isContentDiff: true,
        isCompleteCheck: true,
        isAsync: true,
        checks: [
            {
                id: 'PUBLISHED_CONTENT_DIFF_AUDIT', caption: {en: 'Compare published question configs', fr: 'Compare published question configs'}, informativeOnly: true, checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.PUBLISHED_CONTENT_DIFF_AUDIT
                }
            },
        ],
    },

    //! DEPRECIATED { 
    //     count: ++count,
    //     slug: 'TEI_EA_ANSWER_AUDIT',
    //     itemScope: [AuditQuestionScope.ITEMS_SCORED_MINUS_HS],
    //     caption: {en: 'TEI Expected Answer Audit', fr: 'TEI Expected Answer Audit'},
    //     checks: [
    //         {id: 'MISSING_EXPECTED_ANSWER', caption: {en: 'Items missing expected answer', fr: 'Items missing expected answer'}, },
    //         {id: 'INVALID_TEI_EA_FOR_MCQ_BLOCK', caption: {en: 'MCQ items with TEI_EA defined (Should be null)', fr: 'MCQ items with TEI_EA defined (Should be null)'}, },
    //         {id: 'MISS_MATCH_TEI_EA', caption: {en: 'Item\'s simulated submission doesn\'t match `TEI_EA` param or it has partial simulated score', fr: 'Item\'s simulated submission doesn\'t match `TEI_EA` param or it has partial simulated score'}, },
    //         // {id: CheckId.MISSING_EXPECTED_ANSWER_FOR_MCQ_BLOCK, caption: {en: 'MCQ items missing `EA` param', fr: 'MCQ items missing `EA` param'}, items: [], type: EAuditElementType.QUESTION},
    //         // {id: CheckId.INVALID_EXPECTED_ANSWER_FOR_MCQ_BLOCK, caption: {en: 'MCQ items with invalid expected answer defined', fr: 'MCQ items with invalid expected answer defined'}, items: [], type: EAuditElementType.QUESTION},
    //         // {id: CheckId.MISS_MATCH_EXPECTED_ANSWER_FOR_MCQ, caption: {en: 'MCQ items configuration doesn\'t match `EA` param', fr: 'MCQ items configuration doesn\'t match `EA` param'}, items: [], type: EAuditElementType.QUESTION},
    //     ]
    // },
    {
        auditType: 'DATA',
        slug: 'QUESTIONNAIRE_ITEMS_AUDIT',
        itemScope: [AuditQuestionScope.ITEMS_SURVEY],
        requiredFrameworkFlags: [AuditAssesmentTypeFlags.QUESTIONNAIRE],
        isCompleteCheck: true,
        caption: {en: 'Questionnaire Items Audit', fr: 'Questionnaire Items Audit'},
        checks: [
            {
                id: 'ITEMS_MISSING_IS_QUESTIONNAIRE_CHECK', caption: {en: 'Questionnaire Items missing isQuestionnaire Checkbox ', fr: 'Questionnaire Items missing isQuestionnaire Checkbox '},
                checkType: AuditCheckType.ITEM_SETTINGS,
                checkConfig: {
                    targetProp: 'isQuestionnaire',
                    filterMethod: EFilterMethods.IS_FALSEY
                }
            },
            {
                id: 'ITEMS_WITH_SELECTION_TABLE_SPLIT_DATA_CHECKBOX', caption: {en: 'Questionnaire Items with Selection table split data enabled', fr: 'Questionnaire Items with Selection table split data enabled'},
                checkType: AuditCheckType.BY_BLOCK_TYPE,
                checkConfig: {
                    relativePropPath: 'splitData',
                    filterMethod: EFilterMethods.IS_TRUTHY,
                    targetBlockType: ElementType.SELECT_TABLE
                },
                informativeOnly: true
            },
            {
                id: 'ITEMS_MISSING_SELECTION_TABLE_SPLIT_DATA_CHECKBOX', caption: {en: 'Questionnaire Items with Selection table split data disabled', fr: 'Questionnaire Items with Selection table split data disabled'},
                checkType: AuditCheckType.BY_BLOCK_TYPE,
                checkConfig: {
                    relativePropPath: 'splitData',
                    filterMethod: EFilterMethods.IS_FALSEY,
                    targetBlockType: ElementType.SELECT_TABLE
                }
            },
            {
                id: 'ITEMS_WITHOUT_SELECTION_TABLE_MAX_ONE_SELECTION_PER_ROW', caption: {en: 'Questionnaire Items without Selection table max one selection per row', fr: 'Questionnaire Items without Selection table max one selection per row'},
                checkType: AuditCheckType.BY_BLOCK_TYPE,
                checkConfig: {
                    relativePropPath: 'maxCheckedPerRow',
                    filterMethod: EFilterMethods.IS_FALSEY,
                    targetBlockType: ElementType.SELECT_TABLE,
                }
            },
            {
                id: 'ITEMS_WITH_SELECTION_TABLE_MAX_ONE_SELECTION_PER_ROW', caption: {en: 'Questionnaire Items with Selection table max one selection per row', fr: 'Questionnaire Items with Selection table max one selection per row'},
                checkType: AuditCheckType.BY_BLOCK_TYPE,
                checkConfig: {
                    relativePropPath: 'maxCheckedPerRow',
                    filterMethod: EFilterMethods.EQUAL,
                    targetBlockType: ElementType.SELECT_TABLE,
                    filterValue: 1
                },
                informativeOnly: true
            },
            {
                id: 'ITEMS_WITH_SELECTION_TABLE_MULTIPLE_SELECTION_PER_ROW', caption: {en: 'Questionnaire Items Selection table Multiple selection per row', fr: 'Questionnaire Items Selection table Multiple selection per row'},
                checkType: AuditCheckType.BY_BLOCK_TYPE,
                checkConfig: {
                    relativePropPath: 'maxCheckedPerRow',
                    filterMethod: EFilterMethods.GREATER_THAN,
                    targetBlockType: ElementType.SELECT_TABLE,
                    filterValue: 1
                }
            },
        ],
    },

    {
        auditType: 'DATA',
        slug: 'REJECTED_ITEMS_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS], // todo: this was coded to grab all screens before, why?
        caption: {en: 'Rejected Items Audit', fr: 'Rejected Items Audit'},
        isCompleteCheck: true,
        checks: [
            {
                id: 'REJECTED_ITEMS_INCLUDED_IN_TEST_DESIGNS',
                caption: {en: 'Rejected Items are included in Test designs / panels ', fr: 'Rejected Items are included in Test designs / panels '},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'REJ',
                    filterMethod: EFilterMethods.IS_TRUTHY,
                }
            },
        ],
    },
    {
        auditType: 'DATA',
        slug: 'FIELD_TRIAL_ITEMS_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS], // todo: shouldnt it be ITEMS_SCORED FT? previously was running on all screens
        caption: {en: 'Field Trial Items Audit', fr: 'Field Trial Items Audit'},
        isCompleteCheck: true,
        commonCheckProps: {
            informativeOnly: true,
            excludedItems: ['QUESTIONNAIRE']
        },
        checks: [
            {
                id: 'FIELD_ITEMS_INCLUDED_IN_TEST_DESIGNS',
                caption: {en: 'Field Trial Items are included in Test designs / panels', fr: 'Field Trial Items are included in Test designs / panels'},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'FT', // todo: this prop is not actually designated as a universal embedded field trial flag, but it is sort of being used as if it is, this might be a good thing to store in the style profile
                    filterMethod: EFilterMethods.IS_TRUTHY,
                }
            },
            {
                id: "FIELD_ITEMS_EXCLUDED_IN_TEST_DESIGNS",
                caption: {en: 'Field Trial Items that are not included in Test designs / panels', fr: 'Field Trial Items that are not included in Test designs / panels'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.FIELD_ITEMS_EXCLUDED_IN_TEST_DESIGNS
                }
            },
            {
                id: "FIELD_ITEMS_IN_FT_TESTLETS_IN_NON_FT_TESTLET",
                caption: {en: 'Items from Field Trial testlets duplicated in none Field Trial testlets', fr: 'Items from Field Trial testlets duplicated in none Field Trial testlets'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.FIELD_ITEMS_IN_FT_TESTLETS_IN_NON_FT_TESTLET
                }
            }
        ],
    },
    {
        auditType: 'CONTENT',
        slug: 'VIDEO_ITEM_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS, AuditQuestionScope.ITEM_BANK],
        caption: {en: 'Video Items Audit', fr: 'Video Items Audit'},
        isCompleteCheck: true,
        requiredFrameworkFlags: [AuditAssesmentTypeFlags.IS_RESOURCE_ONLY],
        commonCheckProps: {
            checkElType: 'ASSET',
            checkType: AuditCheckType.BY_BLOCK_TYPE,
            checkConfig: {
                targetBlockType: ElementType.VIDEO, // todo:db: convert before going into DB (but maintain type controls in there)
                relativePropPath: 'urls',
                transforms: [{ type: 'URL_TO_TYPES' }],
            }
        },
        checks: [
            {
                id: 'MISSING_URLS',
                caption: {en: 'Items missing URLS for video src', fr: 'Items missing URLS for video src'},
                checkConfig: {
                    setMissing: ['webm', 'mp4'],
                }
            },
            {
                id: 'MISSING_WEBM',
                caption: {en: 'Items missing WEBM format video source in URLs', fr: 'Items missing WEBM format video source in URLs'},
                checkConfig: {
                    setMissing: ['webm']
                }
            },
            {
                id: 'MISSING_MP4',
                caption: {en: 'Items missing MP4 format video source in URLs', fr: 'Items missing MP4 format video source in URLs'},
                checkConfig: {
                    setMissing: ['mp4']
                }
            }
            // { id: CheckId.SRC_UNAVAIL, caption: {en: 'have one of the sources unavailable', fr: 'have one of the sources unavailable'}, items: [], type: EAuditElementType.ASSET },
        ]
    },
    {
        auditType: 'CONTENT',
        slug: 'BLANK_INVISIBLE_ENTRIES_ITEMS_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS], // AuditQuestionScope.SCREENS
        isCompleteCheck: true,
        caption: {en: 'Check for Blank/Invisible Entries Items Audit', fr: 'Check for Blank/Invisible Entries Items Audit'},
        isCompleteFix: true,
        commonCheckProps: {
            checkType: AuditCheckType.BY_BLOCK_TYPE,
            checkConfig: {
                filterMethod: EFilterMethods.IS_ARR_EMPTY,
            }
        },
        checks: [
            {
                id: 'MCQ_MISSING_OPTIONS',
                caption: {en: 'Items that contain an MCQ with no options to select', fr: 'Items that contain an MCQ with no options to select'},
                checkConfig: {
                    targetBlockType: ElementType.MCQ, // todo:db: 
                    relativePropPath: 'options',
                },
            },
            {
                id: 'SELECTION_TABLE_MISSING_ROWS',
                caption: {en: 'Items that contain a selection table with no rows', fr: 'Items that contain a selection table with no rows'},
                isHidden: true,
                checkConfig: {
                    targetBlockType: ElementType.SELECT_TABLE, // todo:db: 
                    relativePropPath: 'topRow',
                },
            },
            {
                id: 'SELECTION_TABLE_MISSING_COLS',
                caption: {en: 'Items that contain a selection table with no columns', fr: 'Items that contain a selection table with no columns'},
                isHidden: true,
                checkConfig: {
                    targetBlockType: ElementType.SELECT_TABLE, // todo:db: 
                    relativePropPath: 'leftCol',
                },
            },
            {
                id: 'DRAG_DROP_MISSING_DRAGGABLES_DND',
                caption: {en: 'dnd items missing draggables', fr: 'dnd items missing draggables'},
                checkConfig: {
                    targetBlockType: ElementType.MOVEABLE_DND, // todo:db: 
                    relativePropPath: 'draggables',
                },
            },
            {
                id: 'DRAG_DROP_MISSING_TARGETS_DND',
                caption: {en: 'dnd items missing targets', fr: 'dnd items missing targets'},
                checkConfig: {
                    targetBlockType: ElementType.MOVEABLE_DND, // todo:db: 
                    relativePropPath: 'targets',
                },
            },
            {
                id: 'DRAG_DROP_MISSING_DRAGGABLES_GROUPING',
                caption: {en: 'grouping items missing draggables', fr: 'grouping items missing draggables'},
                checkConfig: {
                    targetBlockType: ElementType.GROUPING, // todo:db: 
                    relativePropPath: 'draggables',
                },
            },
            {
                id: 'DRAG_DROP_MISSING_TARGETS_GROUPING',
                caption: {en: 'grouping items missing targets', fr: 'grouping items missing targets'},
                checkConfig: {
                    targetBlockType: ElementType.GROUPING, // todo:db: 
                    relativePropPath: 'targets',
                },
            },
            {
                id: 'DRAG_DROP_MISSING_DRAGGABLES_SORTING',
                caption: {en: 'sorting items missing draggables', fr: 'sorting items missing draggables'},
                checkConfig: {
                    targetBlockType: ElementType.ORDER, // todo:db: 
                    relativePropPath: 'options',
                },
            },
            {
                id: 'DRAG_DROP_MISSING_DRAGGABLES_INSERTION',
                caption: {en: 'insertion items missing draggables', fr: 'insertion items missing draggables'},
                checkConfig: {
                    targetBlockType: ElementType.INSERTION, // todo:db: 
                    relativePropPath: 'draggables',
                },
            },
            {
                id: 'DRAG_DROP_MISSING_TARGETS_INSERTION',
                caption: {en: 'insertion items missing targets', fr: 'insertion items missing targets'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.DRAG_DROP_MISSING_TARGETS_INSERTION
                }
            },
        ]
    },
    {
        auditType: 'CONTENT',
        slug: 'DRAG_DROP_AUDIT_MISSING_KEYS',
        itemScope: [AuditQuestionScope.SCREENS],
        caption: {en: 'Drag Drop Items Audit (missing keys)', fr: 'Drag Drop Items Audit (missing keys)'},
        isCompleteCheck: true,
        commonCheckProps: {
            checkType: AuditCheckType.BY_BLOCK_TYPE,
            checkConfig: {}
        },
        checks: [
            {
                id: 'MISSING_KEY_ID',
                caption: {en: 'Items that contain a Drag-Drop missing key ID(s)', fr: 'Items that contain a Drag-Drop missing key ID(s)'},
                checkConfig: {
                    dndKeys: ['hasMissingKeyIds'],
                },
                informativeOnly: true
            },
            {
                id: 'ITEMS_WITH_DUPLICATE_KEY_ID',
                caption: {en: 'Items that contain a Drag-Drop has duplicate key ID(s)', fr: 'Items that contain a Drag-Drop has duplicate key ID(s)'},
                checkConfig: {
                    dndKeys: ['hasDuplicateKeyIds'],
                },
                informativeOnly: true
            },

            {
                id: 'MISSING_HOME_TARGET_ID',
                caption: {en: 'Items that contain a Drag-Drop missing home target Ids', fr: 'Items that contain a Drag-Drop missing home target Ids'},
                checkConfig: {
                    dndKeys: ['hasMissingHomeTargetIds'],
                },
            },
            {
                id: 'ITEMS_WITH_DUPLICATE_HOME_TARGET_ID',
                caption: {en: 'Items that contain a Drag-Drop have duplicate home target Ids', fr: 'Items that contain a Drag-Drop have duplicate home target Ids'},
                checkConfig: {
                    dndKeys: ['hasDuplicateHomeTargetIds'],
                },
            }
            //  INVALID_NUMBER_OF_DRAGGABLES_TARGETS
        ],
    },
    {
        auditType: 'CONTENT',
        slug: 'DRAG_DROP_AUDIT_MISSING_DIMENSIONS',
        itemScope: [AuditQuestionScope.ITEMS_SCORED],
        caption: {en: 'Drag Drop Items Audit (Missing Dimensions)', fr: 'Drag Drop Items Audit (Missing Dimensions)'},
        isCompleteCheck: true,
        commonCheckProps: {
            checkType: AuditCheckType.BY_BLOCK_TYPE,
            checkConfig: {
                targetBlockType: ElementType.MOVEABLE_DND,
                filterMethod: EFilterMethods.IS_NAN,
            }
        },
        checks: [

            {
                id: 'MISSING_HEIGHT',
                caption: {en: 'Item(s) that contain a Drag\'n\' drop that doesn\'t have a height set', fr: 'Item(s) that contain a Drag\'n\' drop that doesn\'t have a height set'},
                checkConfig: {
                    relativePropPath: 'height',

                },
            }
        ],
    },
    {
        auditType: 'CONTENT',
        slug: 'ITEMS_MISSING_COMPLETION_MARK_CONDITION_AUDIT',
        itemScope: [AuditQuestionScope.ITEMS_SCORED],
        caption: {en: 'Items missing completion condition audit', fr: 'Items missing completion condition audit'},
        commonCheckProps: {
            checkType: AuditCheckType.BY_BLOCK_TYPE, checkConfig: {
                filterMethod: EFilterMethods.IS_NAN
            }
        },
        isCompleteCheck: true,
        checks: [
            {
                id: 'MISSING_COMPLETION_GROUPING', caption: {en: 'Grouping Item(s) missing Mark as Completed in item Config', fr: 'Grouping Item(s) missing Mark as Completed in item Config'}, checkConfig: {
                    targetBlockType: ElementType.GROUPING,
                    relativePropPath: 'reqMinimumPlacedTarget',
                }
            },
            {
                id: 'LESS_DRAGGABLES_THAN_MARK_COMPLETED_GROUPING', caption: {en: 'Grouping Item(s) have fewer draggables than the Mark as Completed value', fr: 'Grouping Item(s) have fewer draggables than the Mark as Completed value'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_BLOCK_TYPE,
                        checkConfig: {
                            relativePropPath: 'isOptionsReusable',
                            targetBlockType: ElementType.GROUPING,
                            filterMethod: EFilterMethods.IS_FALSEY
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_NUMERIC,
                            functionParams: {
                                pathToPropA: 'reqMinimumPlacedTarget',
                                pathToPropB: 'draggables',
                                comparisonMethod: EFilterMethods.GREATER_THAN,
                                targetBlock: ElementType.GROUPING
                            }
                        }
                    }
                ]
            },
            {
                id: 'MORE_DRAGGABLES_THAN_MARK_COMPLETED_GROUPING', caption: {en: 'Grouping Item(s) have more draggables than the Mark as Completed value', fr: 'Grouping Item(s) have more draggables than the Mark as Completed value'},
                informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_NUMERIC,
                    functionParams: {
                        pathToPropA: 'draggables',
                        pathToPropB: 'reqMinimumPlacedTarget',
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        targetBlock: ElementType.GROUPING
                    }
                }
            },
            {
                id: 'LESS_DRAGGABLES_THAN_TARGETS_GROUPING', caption: {en: 'Grouping Item(s) have fewer draggables than targets and do not have the options reusable box checked off', fr: 'Grouping Item(s) have fewer draggables than targets and do not have the options reusable box checked off'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_BLOCK_TYPE,
                        checkConfig: {
                            relativePropPath: 'isOptionsReusable',
                            filterMethod: EFilterMethods.IS_FALSEY,
                            targetBlockType: ElementType.GROUPING,
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_NUMERIC,
                            functionParams: {
                                pathToPropA: 'targets',
                                pathToPropB: 'draggables',
                                comparisonMethod: EFilterMethods.GREATER_THAN,
                                targetBlock: ElementType.GROUPING
                            }
                        }
                    }
                ]
            },
            {
                id: 'MORE_DRAGGABLES_THAN_TARGETS_GROUPING', caption: {en: 'Grouping Item(s) have more draggables than targets', fr: 'Grouping Item(s) have more draggables than targets'},
                informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_NUMERIC,
                    functionParams: {
                        pathToPropA: 'draggables',
                        pathToPropB: 'targets',
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        targetBlock: ElementType.GROUPING
                    }
                }
            },
            {
                id: 'MISSING_COMPLETION_DND', caption: {en: 'Drag\`n\`Drop Item(s) missing Mark as Completed in item Config', fr: 'Drag\`n\`Drop Item(s) missing Mark as Completed in item Config'}, checkConfig: {
                    targetBlockType: ElementType.MOVEABLE_DND,
                    relativePropPath: 'howManyToFill',
                }
            },
            {
                id: 'LESS_DRAGGABLES_THAN_MARK_COMPLETED_DND', caption: {en: 'Drag\'n\'Drop Item(s) have fewer draggables than the Mark as Completed value', fr: 'Drag\'n\'Drop Item(s) have fewer draggables than the Mark as Completed value'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_BLOCK_TYPE,
                        checkConfig: {
                            relativePropPath: 'isOptionsReusable',
                            targetBlockType: ElementType.MOVEABLE_DND,
                            filterMethod: EFilterMethods.IS_FALSEY
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_NUMERIC,
                            functionParams: {
                                pathToPropA: 'howManyToFill',
                                pathToPropB: 'draggables',
                                comparisonMethod: EFilterMethods.GREATER_THAN,
                                targetBlock: ElementType.MOVEABLE_DND
                            }
                        }
                    }
                ]
            },
            {
                id: 'MORE_DRAGGABLES_THAN_MARK_COMPLETED_DND', caption: {en: 'Drag\'n\'Drop Item(s) have more draggables than the Mark as Completed value', fr: 'Drag\'n\'Drop Item(s) have more draggables than the Mark as Completed value'},
                informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_NUMERIC,
                    functionParams: {
                        pathToPropA: 'draggables',
                        pathToPropB: 'howManyToFill',
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        targetBlock: ElementType.MOVEABLE_DND
                    }
                }
            },
            {
                id: 'LESS_DRAGGABLES_THAN_TARGETS_DND', caption: {en: 'Drag\'n\'Drop Item(s) have fewer draggables than targets and do not have the options reusable box checked off', fr: 'Drag\'n\'Drop Item(s) have fewer draggables than targets and do not have the options reusable box checked off'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_BLOCK_TYPE,
                        checkConfig: {
                            relativePropPath: 'isOptionsReusable',
                            targetBlockType: ElementType.MOVEABLE_DND,
                            filterMethod: EFilterMethods.IS_FALSEY
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_NUMERIC,
                            functionParams: {
                                pathToPropA: 'howManyToFill',
                                pathToPropB: 'draggables',
                                comparisonMethod: EFilterMethods.GREATER_THAN,
                                targetBlock: ElementType.MOVEABLE_DND
                            }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_NUMERIC,
                            functionParams: {
                                pathToPropA: 'targets',
                                pathToPropB: 'draggables',
                                comparisonMethod: EFilterMethods.GREATER_THAN,
                                targetBlock: ElementType.MOVEABLE_DND
                            }
                        }
                    }
                ]
            },
            {
                id: 'MORE_DRAGGABLES_THAN_TARGETS_DND', caption: {en: 'Drag\'n\'Drop Item(s) have more draggables than targets', fr: 'Drag\'n\'Drop Item(s) have more draggables than targets'},
                informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_NUMERIC,
                    functionParams: {
                        pathToPropA: 'draggables',
                        pathToPropB: 'targets',
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        targetBlock: ElementType.MOVEABLE_DND
                    }
                }
            },
            {
                id: 'MORE_DRAGGABLES_THAN_TARGETS_INSERTION', caption: {en: 'Insertion Item(s) have more draggables than blanks (informative only)', fr: 'Insertion Item(s) have more draggables than blanks (informative only)'},
                informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_NUMERIC,
                    functionParams: {
                        pathToPropA: 'textBlocks',
                        pathToPropB: 'draggables',
                        comparisonMethod: EFilterMethods.LESS_THAN,
                        targetBlock: ElementType.INSERTION,
                        arrayFilters: [
                            {
                                configTarget: 'pathToPropA',
                                target: 'element.elementType',
                                filterMethod: EFilterMethods.INCLUDES,
                                val: [ElementType.BLANK, ElementType.BLANK_DEPRECIATED]
                            }
                        ]
                    }
                }


            },
            {
                id: 'MORE_TARGETS_THAN_FILLABLE_INSERTION', caption: {en: 'Insertion Item(s) have more targets than can be filled', fr: 'Insertion Item(s) have more targets than can be filled'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_NUMERIC,
                    functionParams: {
                        pathToPropA: 'textBlocks',
                        pathToPropB: 'draggables',
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        targetBlock: ElementType.INSERTION,
                        arrayFilters: [
                            {
                                configTarget: 'pathToPropA',
                                target: 'element.elementType',
                                filterMethod: EFilterMethods.INCLUDES,
                                val: [ElementType.BLANK, ElementType.BLANK_DEPRECIATED]
                            }
                        ]
                    }
                }
            },
        ]
    },
    {
        auditType: 'CONTENT',
        slug: 'FILE_EXTENTION_ITEM_AUDIT',
        caption: {en: 'File Extension audit', fr: 'File Extension audit'},
        itemScope: [AuditQuestionScope.SCREENS, AuditQuestionScope.ITEM_BANK],
        isCompleteCheck: true,
        commonCheckProps: {
            checkType: AuditCheckType.BY_BLOCK_TYPE,
            checkElType: 'ASSET',
            checkConfig: {
                targetBlockType: ElementType.IMAGE,
                transforms: [{ type: 'URL_TO_TYPES' }],
                setHas: ['svg']
            },
            informativeOnly: true
        },
        checks: [
            {
                id: 'DEFAULT_CONTAINS_SVG', caption: {en: 'Items contain default images that are SVGs', fr: 'Items contain default images that are SVGs'}, checkConfig: {
                    relativePropPath: 'images.default.image.url',
                }
            },
            {
                id: 'HIGH_CONTRAST_CONTAINS_SVG', caption: {en: 'Items contain high contrast images that are SVGs', fr: 'Items contain high contrast images that are SVGs'}, checkConfig: {
                    relativePropPath: 'images.default.image.hiContrastImg.url',
                }
            }
        ]
    },
    {
        auditType: 'DATA',
        slug: 'IS_RESPONDABLE_ITEM_AUDIT',
        caption: {en: 'Is Respondable audit', fr: 'Is Respondable audit'},
        itemScope: [AuditQuestionScope.SCREENS, AuditQuestionScope.ITEM_BANK],
        isCompleteCheck: true,
        checks: [
            {
                id: 'FT_BUT_NOT_IS_RESPONDABLE', caption: {en: 'Items that have FT checked or FT_Pool set but not Is_Respondable', fr: 'Items that have FT checked or FT_Pool set but not Is_Respondable'},
                excludedItems: ['READING_SELECTIONS'],
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'is_respondable',
                            filterMethod: EFilterMethods.IS_FALSEY
                        }
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.PROP_VAL,
                                checkConfig: {
                                    targetProp: 'FT_Pool',
                                    filterMethod: EFilterMethods.IS_TRUTHY
                                }
                            },
                            {
                                checkType: AuditCheckType.PROP_VAL,
                                checkConfig: {
                                    targetProp: 'FT',
                                    filterMethod: EFilterMethods.IS_TRUTHY
                                }
                            }
                        ]
                    }
                ]
            },
            {
                id: 'OPERATIONS_ISTATUS_NOT_IS_RESPONDABLE', caption: {en: 'Items that have Operational as iStatus but not Is_Respondable', fr: 'Items that have Operational as iStatus but not Is_Respondable'},
                checkType: AuditCheckType.PROPS_CONFIG,
                checkConfig: [
                    {
                        targetProp: 'is_respondable',
                        filterMethod: EFilterMethods.IS_FALSEY,
                    },
                    {
                        targetProp: 'IStatus',
                        filterMethod: EFilterMethods.EQUAL,
                        filterValue: 'Operational'

                    }
                ],
                excludedItems: ['READING_SELECTIONS']
            },
            {
                id: 'READING_SEL_OR_QUESTIONNAIRE_AND_IS_RESPONDABLE', caption: {en: 'Items that have reading selection/questionnaire and is_respondable checked off', fr: 'Items that have reading selection/questionnaire and is_respondable checked off'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'is_respondable',
                            filterMethod: EFilterMethods.IS_TRUTHY
                        }
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.ITEM_SETTINGS,
                                checkConfig: {
                                    targetProp: 'isReadingSelectionPage',
                                    filterMethod: EFilterMethods.IS_TRUTHY
                                }
                            },
                            {
                                checkType: AuditCheckType.ITEM_SETTINGS,
                                checkConfig: {
                                    targetProp: 'isQuestionnaire',
                                    filterMethod: EFilterMethods.IS_TRUTHY
                                }
                            }
                        ]
                    }
                ]
            },
        ]
    },
    {
        auditType: 'DATA',
        slug: 'OVERALL_EXPECTATION_AUDIT',
        caption: {en: 'Overall expectation audit', fr: 'Overall expectation audit'},
        itemScope: [AuditQuestionScope.ITEMS_SCORED],
        isCompleteCheck: true,
        commonCheckProps: {
            checkType: AuditCheckType.PROPS_CONFIG
        },
        checks: [
            {
                id: 'OE_NOE_NOT_FILLED', caption: {en: 'Items do not have OE or NOE filled in', fr: 'Items do not have OE or NOE filled in'}, checkConfig: [
                    { targetProp: 'OE', filterMethod: EFilterMethods.IS_FALSEY },
                    { targetProp: 'NOE', filterMethod: EFilterMethods.IS_FALSEY }
                ]
            },
            {
                id: '2023_OE_NOT_FILLED', caption: {en: 'Items do not have 2023_OE filled in', fr: 'Items do not have 2023_OE filled in'}, checkConfig: [
                    { targetProp: '2023_OE', filterMethod: EFilterMethods.IS_FALSEY },
                ]
            },
        ]
    },
    {
        auditType: 'DATA',
        slug: 'SPECIFIC_EXPECTATION_AUDIT',
        caption: {en: 'Specific expectation audit', fr: 'Specific expectation audit'},
        itemScope: [AuditQuestionScope.ITEMS_SCORED],
        isCompleteCheck: true,
        commonCheckProps: {
            checkType: AuditCheckType.PROPS_CONFIG
        },
        checks: [
            {
                id: 'SE_NOT_FILLED_IN', caption: {en: 'Items do not have one of SE, SER, SEW or NSE filled in', fr: 'Items do not have one of SE, SER, SEW or NSE filled in'}, checkConfig: [
                    { targetProp: 'SE', filterMethod: EFilterMethods.IS_FALSEY },
                    { targetProp: 'SER', filterMethod: EFilterMethods.IS_FALSEY },
                    { targetProp: 'SEW', filterMethod: EFilterMethods.IS_FALSEY },
                    { targetProp: 'NSE', filterMethod: EFilterMethods.IS_FALSEY },
                ]
            },
            {
                id: 'TWENTY_THREE_SER_NOT_FILLED_IN', caption: {en: 'Items that do not have 2023_SER filled in', fr: 'Items that do not have 2023_SER filled in'}, checkConfig: [
                    { targetProp: '2023_SER', filterMethod: EFilterMethods.IS_FALSEY },
                ]
            },
            {
                id: 'TWENTY_THREE_SEW_NOT_FILLED_IN', caption: {en: 'Items that do not have 2023_SEW filled in', fr: 'Items that do not have 2023_SEW filled in'}, checkConfig: [
                    { targetProp: '2023_SEW', filterMethod: EFilterMethods.IS_FALSEY },
                ]
            },
        ]
    },
    {
        auditType: 'DATA',
        slug: 'PASSAGE_AUDIT',
        caption: {en: 'Passage Block audit', fr: 'Passage Block audit'},
        itemScope: [AuditQuestionScope.SCREENS, AuditQuestionScope.ITEM_BANK],
        isCompleteCheck: true,
        checks: [
            {
                id: 'CONTAINS_PASSAGE_AUDIT', caption: {en: 'Items that contain a passage block in test designs', fr: 'Items that contain a passage block in test designs'}, checkType: AuditCheckType.BY_BLOCK_TYPE, checkConfig: {
                    targetBlockType: ElementType.PASSAGE,
                    relativePropPath: 'elementType',
                    filterMethod: EFilterMethods.EQUAL,
                    filterValue: ElementType.PASSAGE

                },
                excludedItems: ['QUESTIONNAIRE'],
                informativeOnly: true,
            },
        ]
    },
    {
        auditType: 'DATA',
        slug: 'PJ_SCAN_AUDIT',
        caption: {en: 'PJ Scan Audit', fr: 'PJ Scan Audit'},
        itemScope: [AuditQuestionScope.SCREENS, AuditQuestionScope.SCREENS_INCLUDE_DISABLED],
        isCompleteCheck: true,
        commonCheckProps: {
            excludedItems: ['QUESTIONNAIRE', 'READING_SELECTIONS']
        },
        requiredFrameworkParamCodes: ["SCAN_ID"],
        checks: [
            {
                id: 'MISSING_SCAN_SLUG', caption: {en: 'Items that have scan slug missing', fr: 'Items that have scan slug missing'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.CONTAINS_DUAL_TEXT_INPUT,
                        }
                    },
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'SCAN_ID',
                            filterMethod: EFilterMethods.IS_FALSEY
                        }
                    }
                ]
            },
            {
                id: 'NONE_HUMAN_SCORED_HAS_SCAN_SLUG', caption: {en: 'Items that have scan slug but are not human scored', fr: 'Items that have scan slug but are not human scored'}, excludedItems: ['QUESTIONNAIRE', 'READING_SELECTIONS', 'HUMAN_SCORED'],
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'SCAN_ID',
                    filterMethod: EFilterMethods.IS_TRUTHY
                }
            },
            {
                id: 'INCORRECTLY_MAPPED_SCAN_SLUG', caption: {en: 'Items that have scan slug incorrectly mapped', fr: 'Items that have scan slug incorrectly mapped'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.CONTAINS_DUAL_TEXT_INPUT
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.INCORRECTLY_MAPPED_SCAN_SLUG,
                            functionParams: {
                                scanParam: 'SCAN_ID',
                                sectionToValidSlugMap: {
                                    0: ['SESSION_A'],
                                    1: ['SESSION_B'],
                                    2: ['SESSION_C'],
                                    3: ['SESSION_DR', 'SESSION_DW']
                                },
                                sectionToValidSSlugMap: {
                                    'SESSION_DR': ['IN', 'LN'],
                                    'SESSION_DW': ['SW']
                                }
                            }
                        }
                    }
                ]
            },
            {
                id: 'MISSING_PAPER_TEXT', caption: {en: 'Items that have paper text or voiceover missing', fr: 'Items that have paper text or voiceover missing'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.CONTAINS_DUAL_TEXT_INPUT
                        }
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        val: 0,
                                        comparisonMethod: EFilterMethods.GREATER_THAN,
                                        targetBlocks: [ElementType.INPUT],
                                        arrayFilters: [
                                            {
                                                target: 'paperVoiceOver.url',
                                                filterMethod: EFilterMethods.IS_NULL,
                                            },
                                            {
                                                target: 'isDual',
                                                filterMethod: EFilterMethods.IS_TRUTHY
                                            },
                                            {
                                                target: 'format',
                                                filterMethod: EFilterMethods.EQUAL,
                                                val: 'text'
                                            }
                                        ]
                                    }
                                }
                            },
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        val: 0,
                                        comparisonMethod: EFilterMethods.GREATER_THAN,
                                        targetBlocks: [ElementType.INPUT],
                                        arrayFilters: [
                                            {
                                                target: 'paperVoiceOver.script',
                                                filterMethod: EFilterMethods.IS_NULL
                                            },
                                            {
                                                target: 'isDual',
                                                filterMethod: EFilterMethods.IS_TRUTHY
                                            },
                                            {
                                                target: 'format',
                                                filterMethod: EFilterMethods.EQUAL,
                                                val: 'text'
                                            }
                                        ]
                                    }
                                }
                            },
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        val: 0,
                                        comparisonMethod: EFilterMethods.GREATER_THAN,
                                        targetBlocks: [ElementType.INPUT],
                                        arrayFilters: [
                                            {
                                                target: 'paperText',
                                                filterMethod: EFilterMethods.IS_NULL
                                            },
                                            {
                                                target: 'isDual',
                                                filterMethod: EFilterMethods.IS_TRUTHY
                                            },
                                            {
                                                target: 'format',
                                                filterMethod: EFilterMethods.EQUAL,
                                                val: 'text'
                                            }
                                        ]
                                    }
                                }
                            }
                        ]

                    }
                ]
            },
            {
                id: 'SCAN_ID_PRESERVED_PARAMS', caption: {en: 'Assessment framework has no configured SCAN_ID in preserved params', fr: 'Assessment framework has no configured SCAN_ID in preserved params'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.FRAMEWORK_CONFIG,
                    functionParams: {
                        target: 'preservedMetaParams',
                        filterMethod: EFilterMethods.TARGET_DOES_NOT_INCLUDE,
                        val: 'SCAN_ID'
                    }
                }
            },
        ]
    },
    {
        auditType: 'DATA',
        slug: 'SKILL',
        caption: {en: 'Skill', fr: 'Skill'},
        itemScope: [AuditQuestionScope.ITEMS_SCORED, AuditQuestionScope.ITEM_BANK_SCORED],
        isCompleteCheck: true,
        commonCheckProps: {
            checkType: AuditCheckType.PROP_VAL,
            checkConfig: {
                filterMethod: EFilterMethods.IS_FALSEY
            }
        },
        checks: [
            {
                id: 'MISSING_SK_2023_PARAMETER', caption: {en: 'Items that do not contain a 2023_SK parameter', fr: 'Items that do not contain a 2023_SK parameter'}, checkConfig: {
                    targetProp: '2023_SK'
                }
            },
            {
                id: 'MISSING_SKDetail_PARAMETER', caption: {en: 'Items that do not contain a SKDetail parameter', fr: 'Items that do not contain a SKDetail parameter'}, checkType: AuditCheckType.PROPS_CONFIG, checkConfig: [
                    {
                        targetProp: 'SKDetail',
                        filterMethod: EFilterMethods.IS_FALSEY
                    },
                    {
                        targetProp: 'Strand',
                        filterMethod: EFilterMethods.INCLUDES,
                        filterValue: ['Writing', 'Écriture']
                    }
                ]
            },
            {
                id: 'MISSING_SC_PARAMETER', caption: {en: 'Items that do not contain a SC parameter', fr: 'Items that do not contain a SC parameter'}, checkConfig: {
                    targetProp: 'SC'
                }
            }
        ]
    },
    {
        auditType: 'DATA',
        slug: 'PASSAGE',
        caption: {en: 'Passage', fr: 'Passage'},
        itemScope: [AuditQuestionScope.SCREENS_PASSAGES, AuditQuestionScope.ITEM_BANK_PASSAGES],
        isCompleteCheck: true,
        commonCheckProps: {
            checkType: AuditCheckType.PROP_VAL,
            checkConfig: {
                filterMethod: EFilterMethods.IS_FALSEY
            }
        },
        checks: [
            {
                id: 'MISSING_PARAM_FLAG', caption: {en: 'Passage param is not checked', fr: 'Passage param is not checked'}, checkType: AuditCheckType.PROP_VAL, checkConfig: {
                    targetProp: 'PSG',
                },
                excludedItems: ['NON_READING_SELECTIONS']
            },
            {
                id: 'MISSING_PASSAGEID_PARAMETER', caption: {en: 'Items that do not contain a passageID parameter', fr: 'Items that do not contain a passageID parameter'}, checkConfig: {
                    targetProp: 'passageID',
                },
                excludedItems: ['NO_RESOURCE_OR_SELECTION']
            },
            {
                id: 'MISCONFIGURED_PASSAGEID_PARAMETER', caption: {en: 'Items that contain a misconfigured passageID parameter', fr: 'Items that contain a misconfigured passageID parameter'}, checkConfig: {
                    targetProp: 'passageID',
                    filterMethod: EFilterMethods.NUMBER_MISCONFIGURED
                },
                excludedItems: ['NO_RESOURCE_OR_SELECTION']
            },
            {
                id: 'MISSING_UNIQUE_PASSAGE_ID', caption: {en: 'Items that do not contain a Unique_PassageID parameter', fr: 'Items that do not contain a Unique_PassageID parameter'}, checkConfig: {
                    targetProp: 'Unique_PassageID',
                },
                excludedItems: ['NON_READING_SELECTIONS'],
                informativeOnly: true
            },
            {
                id: 'MISSING_PTYP_PARAMETER', caption: {en: 'Items that do not contain a PTYP parameter', fr: 'Items that do not contain a PTYP parameter'}, checkConfig: {
                    targetProp: 'PTYP',
                },
                excludedItems: ['NON_READING_SELECTIONS']
            },
            {
                id: 'MISSING_P_TITLE_PARAMETER', caption: {en: 'Items that do not contain a P_Title parameter', fr: 'Items that do not contain a P_Title parameter'}, checkConfig: {
                    targetProp: 'P_Title',
                },
                excludedItems: ['NON_READING_SELECTIONS']
            },

        ]
    },
    {
        auditType: 'DATA',
        slug: 'STRAND',
        caption: {en: 'Strand', fr: 'Strand'},
        itemScope: [AuditQuestionScope.ITEMS_SCORED, AuditQuestionScope.ITEM_BANK_SCORED],
        isCompleteCheck: true,
        commonCheckProps: {
            checkType: AuditCheckType.PROP_VAL,
            checkConfig: {
                filterMethod: EFilterMethods.IS_FALSEY
            }
        },
        checks: [
            {
                id: 'MISSING_STRAND_PARAMETER', caption: {en: 'Items that do not contain a Strand parameter', fr: 'Items that do not contain a Strand parameter'}, checkConfig: {
                    targetProp: 'Strand',
                }
            },
            {
                id: 'MISSING_EQAO_COMPONENT_PARAM', caption: {en: 'Items that do not contain a EQAO_Component parameter', fr: 'Items that do not contain a EQAO_Component parameter'}, checkConfig: {
                    targetProp: 'EQAO_Component',
                }
            },
        ]
    },
    {
        auditType: 'DATA',
        slug: 'REPORTING_SUBJECT',
        caption: {en: 'Reporting Subject', fr: 'Reporting Subject'},
        itemScope: [AuditQuestionScope.SCREENS, AuditQuestionScope.ITEM_BANK],
        isCompleteCheck: true,
        checks: [
            {
                id: 'MISSING_REPORTING_SUBJECT', caption: {en: 'Items that do not contain a Reporting_Subject parameter', fr: 'Items that do not contain a Reporting_Subject parameter'}, checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'Reporting_Subject',
                    filterMethod: EFilterMethods.IS_FALSEY
                },
                excludedItems: ["QUESTIONNAIRE"]
            }
        ]
    },
    {
        auditType: 'DATA',
        slug: 'ITEM_MATRIX_AUDIT',
        itemScope: [AuditQuestionScope.ITEMS_SCORED_MINUS_HS],
        caption: {en: 'TEI score Matrix audit', fr: 'TEI score Matrix audit'},
        isCompleteCheck: true,
        isAsync: true,
        checks: [
            {
                id: 'UNCONFIRMED', caption: {en: 'Items that contain unconfirmed matrixes', fr: 'Items that contain unconfirmed matrixes'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                    functionParams: {
                        targetBlocks: scoreMatrixElementTypes,
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        val: 0,
                        arrayFilters: [
                            {
                                target: 'scoreMatrix.isConfirmed',
                                filterMethod: EFilterMethods.IS_FALSEY
                            }
                        ]
                    }
                }
            },
            {
                id: 'OUTDATED', caption: {en: 'Items that contain outdated matrixes', fr: 'Items that contain outdated matrixes'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                    functionParams: {
                        targetBlocks: scoreMatrixElementTypes,
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        val: 0,
                        arrayFilters: [
                            {
                                target: 'scoreMatrix.isUpdated',
                                filterMethod: EFilterMethods.IS_FALSEY
                            }
                        ]
                    }
                }
            },
            {
                id: 'REUSABLE_DRAGGABLES', caption: {en: 'Items that contain reusable options', fr: 'Items that contain reusable options'}, informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                    functionParams: {
                        targetBlocks: [ElementType.GROUPING, ElementType.MOVEABLE_DND],
                        val: 0,
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        arrayFilters: [
                            {
                                target: 'isOptionsReusable',
                                filterMethod: EFilterMethods.IS_TRUTHY
                            }
                        ]
                    }
                }
            },
            {
                id: 'MULTI_COMBINATION', caption: {en: 'Items that accept multiple combinations', fr: 'Items that accept multiple combinations'}, informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                    functionParams: {
                        targetBlocks: [ElementType.MOVEABLE_DND],
                        val: 0,
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        arrayFilters: [
                            {
                                target: 'isAcceptMultipleCombinations',
                                filterMethod: EFilterMethods.IS_TRUTHY
                            }
                        ]
                    }
                }
            },
            {
                id: 'MULTI_TARGET', caption: {en: 'Items that accept multiple correct targets', fr: 'Items that accept multiple correct targets'}, informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                    functionParams: {
                        targetBlocks: [ElementType.MOVEABLE_DND],
                        val: 0,
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        arrayFilters: [
                            {
                                target: 'isMultipleOptionsRight',
                                filterMethod: EFilterMethods.IS_TRUTHY
                            }
                        ]
                    }
                }
            },
            {
                id: 'CUSTOM_VALIDATION', caption: {en: 'Items that use custom validation', fr: 'Items that use custom validation'}, informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                    functionParams: {
                        targetBlocks: [ElementType.MOVEABLE_DND],
                        val: 0,
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        arrayFilters: [
                            {
                                target: 'isCustomValidataion',
                                filterMethod: EFilterMethods.IS_TRUTHY
                            }
                        ]
                    }
                }
            },
            {
                id: 'NON_SCORING_MATRIX', caption: {en: 'Items that have an input element that does not utilize the scoring matrix', fr: 'Items that have an input element that does not utilize the scoring matrix'}, informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.RESPONDABLE_COUNT,
                    functionParams: {
                        excludedBlocks: scoreMatrixElementTypes,
                        val: 0,
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                    }
                }
            },
            {
                id: 'FLAGGED_ITEMS', caption: {en: 'Items that have a flag in the Expected Answers table', fr: 'Items that have a flag in the Expected Answers table'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.ELEMENT_COUNT,
                            functionParams: {
                                targetBlocks: scoreMatrixElementTypes,
                                comparisonMethod: EFilterMethods.EQUAL,
                                val: 1,

                            }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.EXPECTED_ANSWER,
                            functionParams: {
                                arrayFilters: [
                                    {
                                        target: 'is_score_matrix_validated',
                                        filterMethod: EFilterMethods.IS_FALSEY
                                    }
                                ]
                            }
                        }
                    }
                ]
            },
            {
                id: 'Q_EXP_ANS_MULTI_ACC', caption: {en: `contain(s) multiple specified correct responses `, fr: `contain(s) multiple specified correct responses `}, excludedItems: ['QUESTIONNAIRE', 'READING_SELECTIONS'], informativeOnly: true,
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.ELEMENT_COUNT,
                            functionParams: {
                                targetBlocks: scoreMatrixElementTypes,
                                comparisonMethod: EFilterMethods.EQUAL,
                                val: 1,

                            }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.EXPECTED_ANSWER,
                            functionParams: {
                                comparisonMethod: EFilterMethods.GREATER_THAN,
                                count: 1,
                                arrayFilters: [
                                    {
                                        target: 'score',
                                        filterMethod: EFilterMethods.EQUAL,
                                        val: 'weight',
                                        isValPath: true
                                    }
                                ]
                            }
                        }
                    }
                ]
            },
        ]
    },
    {
        auditType: 'ADIF',
        slug: 'ITEM_TYPE_AUDIT',
        auditTarget: AuditTarget.QUESTIONS,
        itemScope: [AuditQuestionScope.ITEMS_SCORED, AuditQuestionScope.ITEM_BANK_SCORED],
        caption: {en: 'Item type audit', fr: 'Item type audit'},
        requiredFrameworkParamCodes: ['ItemType'],
        isCompleteCheck: true,
        checks: [
            {
                id: 'ITEM_TYPE_MISSING', caption: {en: 'Items that dont have item type set', fr: 'Items that dont have item type set'},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'ItemType',
                    filterMethod: EFilterMethods.IS_NULL
                }
            },
            {
                id: 'CLK_NO_MCQS', caption: {en: 'CLK items that are not a single none dropdown MCQ block', fr: 'CLK items that are not a single none dropdown MCQ block'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'ItemType',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: EItemTypesParam.CLK
                        }
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.MCQ],
                                        comparisonMethod: EFilterMethods.NOT_EQUAL,
                                        val: 1,
                                    }
                                }
                            },
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.MCQ],
                                        comparisonMethod: EFilterMethods.NOT_EQUAL,
                                        val: 1,
                                        arrayFilters: [
                                            {
                                                target: 'displayStyle',
                                                filterMethod: EFilterMethods.DOES_NOT_INCLUDE,
                                                val: [McqDisplay.DROPDOWN, McqDisplay.CUSTOM_DROPDOWN, McqDisplay.LIKERT]
                                            }
                                        ]
                                    }
                                }
                            }
                        ]
                    }
                ]
            },
            {
                id: 'DP_NO_MCQS', caption: {en: 'DP items that do not contain a single dropdown MCQ block', fr: 'DP items that do not contain a single dropdown MCQ block'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'ItemType',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: EItemTypesParam.DP
                        }
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.MCQ],
                                        comparisonMethod: EFilterMethods.NOT_EQUAL,
                                        val: 1,
                                    }
                                }
                            },
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.MCQ],
                                        comparisonMethod: EFilterMethods.NOT_EQUAL,
                                        val: 1,
                                        arrayFilters: [
                                            {
                                                target: 'displayStyle',
                                                filterMethod: EFilterMethods.INCLUDES,
                                                val: [McqDisplay.DROPDOWN, McqDisplay.CUSTOM_DROPDOWN]
                                            }
                                        ]
                                    }
                                }
                            }
                        ]
                    }
                ]
            },
            {
                id: 'DDP_NO_TWO_MCQS', caption: {en: 'DDP items that do not contain at least 2 dropdown MCQ blocks', fr: 'DDP items that do not contain at least 2 dropdown MCQ blocks'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'ItemType',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: EItemTypesParam.DDP
                        }
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.MCQ],
                                        comparisonMethod: EFilterMethods.NOT_EQUAL,
                                        val: 2,
                                    }
                                }
                            },
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.MCQ],
                                        comparisonMethod: EFilterMethods.NOT_EQUAL,
                                        val: 2,
                                        arrayFilters: [
                                            {
                                                target: 'displayStyle',
                                                filterMethod: EFilterMethods.INCLUDES,
                                                val: [McqDisplay.DROPDOWN, McqDisplay.CUSTOM_DROPDOWN]
                                            }
                                        ]
                                    }
                                }
                            }
                        ]
                    }
                ]
            },
            {
                id: 'DG_NO_VALID_BLOCKS', caption: {en: 'DG items that do not contain a DnD, Ordering, Grouping, or Insertion block', fr: 'DG items that do not contain a DnD, Ordering, Grouping, or Insertion block'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'ItemType',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: EItemTypesParam.DG
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.ELEMENT_COUNT,
                            functionParams: {
                                targetBlocks: [ElementType.MOVEABLE_DND, ElementType.GROUPING, ElementType.INSERTION, ElementType.ORDER],
                                comparisonMethod: EFilterMethods.EQUAL,
                                val: 0
                            }
                        }
                    }
                ]
            },
            {
                id: 'CH_NO_SELECTION_TABLE', caption: {en: 'CH items that do not contain a Selection table block', fr: 'CH items that do not contain a Selection table block'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'ItemType',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: EItemTypesParam.CH
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.ELEMENT_COUNT,
                            functionParams: {
                                targetBlocks: [ElementType.SELECT_TABLE],
                                comparisonMethod: EFilterMethods.EQUAL,
                                val: 0
                            }
                        }
                    }
                ]
            },
            {
                id: 'SW_NO_SHORT_ANSWER', caption: {en: 'SW items that do not contain a short answer keyboard input', fr: 'SW items that do not contain a short answer keyboard input'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'ItemType',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: EItemTypesParam.SW
                        }
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.INPUT],
                                        comparisonMethod: EFilterMethods.EQUAL,
                                        val: 0,
                                        arrayFilters: [
                                            {
                                                target: 'format',
                                                filterMethod: EFilterMethods.EQUAL,
                                                val: InputFormat.TEXT
                                            }
                                        ]
                                    }
                                }
                            },
                            {
                                checkType: AuditCheckType.ITEM_SETTINGS,
                                checkConfig: {
                                    targetProp: 'readSelections',
                                    filterValue: 0,
                                    filterMethod: EFilterMethods.ARRAY_LENGTH_COMPARISON_GREATER

                                }
                            }

                        ]
                    }
                ]
            },
            {
                id: 'LW_NO_SHORT_ANSWER', caption: {en: 'LW items that do not contain a short answer keyboard input', fr: 'LW items that do not contain a short answer keyboard input'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'ItemType',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: EItemTypesParam.LW
                        }
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.INPUT],
                                        comparisonMethod: EFilterMethods.EQUAL,
                                        val: 0,
                                        arrayFilters: [
                                            {
                                                target: 'format',
                                                filterMethod: EFilterMethods.EQUAL,
                                                val: InputFormat.TEXT
                                            }
                                        ]
                                    }
                                }
                            },
                            {
                                checkType: AuditCheckType.ITEM_SETTINGS,
                                checkConfig: {
                                    targetProp: 'readSelections',
                                    filterValue: 0,
                                    filterMethod: EFilterMethods.ARRAY_LENGTH_COMPARISON_GREATER

                                }
                            }

                        ]
                    }
                ]
            },
            {
                id: 'OR_NO_SHORT_ANSWER', caption: {en: 'OR items that do not contain a short answer keyboard input', fr: 'OR items that do not contain a short answer keyboard input'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'ItemType',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: EItemTypesParam.OR
                        }
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.INPUT],
                                        comparisonMethod: EFilterMethods.EQUAL,
                                        val: 0,
                                        arrayFilters: [
                                            {
                                                target: 'format',
                                                filterMethod: EFilterMethods.EQUAL,
                                                val: InputFormat.TEXT
                                            }
                                        ]
                                    }
                                }
                            },
                            {
                                checkType: AuditCheckType.ITEM_SETTINGS,
                                checkConfig: {
                                    targetProp: 'readSelections',
                                    filterValue: 0,
                                    filterMethod: EFilterMethods.ARRAY_LENGTH_COMPARISON_EQUAL

                                }
                            }

                        ]
                    }
                ]
            },
        ]
    },
    {
        auditType: 'ADIF',
        slug: 'TEI_TYPE2_AUDIT',
        auditTarget: AuditTarget.QUESTIONS,
        itemScope: [AuditQuestionScope.ITEMS_SCORED, AuditQuestionScope.ITEM_BANK_SCORED],
        caption: {en: 'TEI type audit', fr: 'TEI type audit'},
        isCompleteCheck: true,
        requiredFrameworkParamCodes: ['TEI_TYPE2'],
        checks: [
            {
                id: 'ITEM_TYPE2_MISSING', caption: {en: 'Items that dont have TEI_TYPE2 set', fr: 'Items that dont have TEI_TYPE2 set'}, checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'TEI_TYPE2',
                    filterMethod: EFilterMethods.IS_NULL
                }
            },
            {
                id: 'TEI_TYPE2_CLK_NO_MCQS', caption: {en: 'Click Response items that are not a single none dropdown MCQ block', fr: 'Click Response items that are not a single none dropdown MCQ block'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'TEI_TYPE2',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: ETEITypesParam.CLICK_RESPONSE
                        }
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.MCQ],
                                        comparisonMethod: EFilterMethods.NOT_EQUAL,
                                        val: 1,
                                    }
                                }
                            },
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.MCQ],
                                        comparisonMethod: EFilterMethods.NOT_EQUAL,
                                        val: 1,
                                        arrayFilters: [
                                            {
                                                target: 'displayStyle',
                                                filterMethod: EFilterMethods.DOES_NOT_INCLUDE,
                                                val: [McqDisplay.DROPDOWN, McqDisplay.CUSTOM_DROPDOWN, McqDisplay.LIKERT]
                                            }
                                        ]
                                    }
                                }
                            }
                        ]
                    }
                ]
            },
            {
                id: 'TEI_TYPE2_DP_NO_MCQS', caption: {en: 'Drop Down  items that do not contain a single dropdown MCQ block', fr: 'Drop Down  items that do not contain a single dropdown MCQ block'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'TEI_TYPE2',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: ETEITypesParam.DROP_DOWN
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.ELEMENT_COUNT,
                            functionParams: {
                                targetBlocks: [ElementType.MCQ],
                                comparisonMethod: EFilterMethods.EQUAL,
                                val: 0,
                                arrayFilters: [
                                    {
                                        target: 'displayStyle',
                                        filterMethod: EFilterMethods.INCLUDES,
                                        val: [McqDisplay.DROPDOWN, McqDisplay.CUSTOM_DROPDOWN]
                                    }
                                ]
                            }
                        }
                    }
                ]
            },
            {
                id: 'TEI_TYPE2_MS_NO_MULTISELECT', caption: {en: 'Multiple Select items that do not contain more than one MCQ or any MCQ with multiselect enabled.', fr: 'Multiple Select items that do not contain more than one MCQ or any MCQ with multiselect enabled.'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'TEI_TYPE2',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: ETEITypesParam.MULTIPLE_SELECT
                        }
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.MCQ],
                                        comparisonMethod: EFilterMethods.LESS_THAN,
                                        val: 1,
                                    }
                                }
                            },
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.MCQ],
                                        comparisonMethod: EFilterMethods.LESS_THAN,
                                        val: 1,
                                        arrayFilters: [
                                            {
                                                target: 'isMultiSelect',
                                                filterMethod: EFilterMethods.IS_TRUTHY
                                            }
                                        ]
                                    }
                                }
                            }
                        ]
                    }
                ]
            },
            {
                id: 'TEI_TYPE2_DG_NO_VALID_BLOCKS', caption: {en: 'Drag and Drop items that do not contain a DnD, Ordering, Grouping, or Insertion block', fr: 'Drag and Drop items that do not contain a DnD, Ordering, Grouping, or Insertion block'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'TEI_TYPE2',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: ETEITypesParam.DRAG_AND_DROP
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.ELEMENT_COUNT,
                            functionParams: {
                                targetBlocks: [ElementType.MOVEABLE_DND, ElementType.GROUPING, ElementType.INSERTION, ElementType.ORDER],
                                comparisonMethod: EFilterMethods.EQUAL,
                                val: 0
                            }
                        }
                    }
                ]
            },
            {
                id: 'TEI_TYPE2_CH_NO_SELECTION_TABLE', caption: {en: 'Checklist items that do not contain a Selection table block', fr: 'Checklist items that do not contain a Selection table block'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'TEI_TYPE2',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: ETEITypesParam.CHECKLIST
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.ELEMENT_COUNT,
                            functionParams: {
                                targetBlocks: [ElementType.SELECT_TABLE],
                                comparisonMethod: EFilterMethods.EQUAL,
                                val: 0
                            }
                        }
                    }
                ]
            },
        ]
    },
    {
        auditType: 'CONTENT',
        slug: 'ITEM_MAP_MODULE_META_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS_PASSAGES_MAP_MODULE],
        caption: {en: 'Map Module Meta Audit', fr: 'Map Module Meta Audit'},
        isCompleteCheck: true,
        requiredFrameworkFlags: [AuditAssesmentTypeFlags.PRESERVED_PARAMS],
        checks: [
            // are being generated programatically
            {
                id: 'MISSING_RESERVED_META', caption: {en: 'Items missing preserved param', fr: 'Items missing preserved param'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.ITEMS_MISSING_PRESERVED_PARAMS
                        }
                    }
                ]
            }
        ]
    },
    {
        auditType: 'DATA',
        slug: 'ITEM_CATEGORY_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS, AuditQuestionScope.ITEM_BANK],
        isCompleteCheck: true,
        caption: {en: 'item category audit (MPT)', fr: 'item category audit (MPT)'},
        commonCheckProps: {
            checkType: AuditCheckType.PROPS_CONFIG
        },
        checks: [
            {
                id: 'ITEM_CATEGORY_MISSING', caption: {en: 'Items that don\'t have item category set', fr: 'Items that don\'t have item category set'}, checkConfig: [{
                    targetProp: 'Item_Category',
                    filterMethod: EFilterMethods.IS_FALSEY
                }],
                excludedItems: ['READING_SELECTIONS']

            },
            {
                id: 'ITEM_CATEGORY_MATH_MISSMATCH', caption: {en: 'Items with Math category and an incorrect D1 param', fr: 'Items with Math category and an incorrect D1 param'}, checkConfig: [
                    {
                        targetProp: 'Item_Category',
                        filterMethod: EFilterMethods.EQUAL,
                        filterValue: 'Math',
                    },
                    {
                        targetProp: 'D1',
                        filterMethod: EFilterMethods.DOES_NOT_INCLUDE,
                        filterValue: ['NS', 'M', 'PR']
                    }
                ],
                excludedItems: ['QUESTIONNAIRE', 'READING_SELECTIONS']

            },
            {
                id: 'ITEM_CATEGORY_PEDAGOGY_MISSMATCH', caption: {en: 'Items with Pedagogy category and an incorrect D1 param', fr: 'Items with Pedagogy category and an incorrect D1 param'}, checkConfig: [
                    {
                        targetProp: 'Item_Category',
                        filterMethod: EFilterMethods.EQUAL,
                        filterValue: 'Pedagogy'
                    },
                    {
                        targetProp: 'D1',
                        filterMethod: EFilterMethods.DOES_NOT_INCLUDE,
                        filterValue: ['P1', 'P2', 'P3']
                    }
                ],
                excludedItems: ['QUESTIONNAIRE', 'READING_SELECTIONS']
            },
            {
                id: 'ITEM_CATEGORY_QUESTIONNAIRE_MISSMATCH', caption: {en: 'Items with Questionnaire category set and an incorrect D1 param or isn\'t marked as a questionnaire', fr: 'Items with Questionnaire category set and an incorrect D1 param or isn\'t marked as a questionnaire'}, excludedItems: ['READING_SELECTIONS'],
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'Item_Category',
                            filterMethod: EFilterMethods.EQUAL,
                            filterValue: 'Questionnaire'
                        }
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.ITEM_SETTINGS,
                                checkConfig: {
                                    targetProp: 'isQuestionnaire',
                                    filterMethod: EFilterMethods.IS_FALSEY
                                }

                            },
                            {
                                checkType: AuditCheckType.PROP_VAL,
                                checkConfig: {
                                    targetProp: 'D1',
                                    filterMethod: EFilterMethods.INCLUDES,
                                    filterValue: ['S1', 'S2']
                                }
                            }
                        ]
                    }
                ]

            }
        ]
    },
    {
        auditType: 'CONTENT',
        slug: 'SAMPLE_TEST_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS],
        caption: {en: 'Sample Test Audit (PT)', fr: 'Sample Test Audit (PT)'},
        commonCheckProps: {
            checkType: AuditCheckType.PROP_VAL
        },
        isCompleteCheck: true,
        checks: [
            {
                id: 'SAMPLE_ITEMS_WITHOUT_PT', caption: {en: 'Items with PT param set to false', fr: 'Items with PT param set to false'}, checkConfig: {
                    targetProp: 'PT',
                    filterMethod: EFilterMethods.IS_FALSEY
                },
                informativeOnly: 'NOT_SAMPLE_TEST'
            },
            {
                id: 'REAL_ITEMS_WITH_PT', caption: {en: 'Items with PT param set to true', fr: 'Items with PT param set to true'}, checkConfig: {
                    targetProp: 'PT',
                    filterMethod: EFilterMethods.IS_TRUTHY
                },
                informativeOnly: 'SAMPLE_TEST'
            },
            {
                id: 'SAMPLE_ITEMS_WITH_PT_AND_NOT_REL', caption: {en: 'Items that contain a PT parameter and do not contain a REL parameter', fr: 'Items that contain a PT parameter and do not contain a REL parameter'}, checkType: AuditCheckType.PROPS_CONFIG, checkConfig: [
                    {
                        targetProp: 'PT',
                        filterMethod: EFilterMethods.IS_TRUTHY
                    },
                    {
                        targetProp: 'REL',
                        filterMethod: EFilterMethods.IS_FALSEY
                    }
                ]
            }
        ]
    },
    {
        auditType: 'DATA',
        slug: 'RESOURCE_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS],
        isCompleteCheck: true,
        caption: {en: 'Resource Audit', fr: 'Resource Audit'},
        checks: [
            {
                id: 'RESOURCE_ITEMS_WITH_RESPONDABLE', caption: {en: 'Items with respondables and have reading selection set to true', fr: 'Items with respondables and have reading selection set to true'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {}
                        }
                    },
                    {
                        checkType: AuditCheckType.ITEM_SETTINGS,
                        checkConfig: {
                            targetProp: 'isReadingSelectionPage',
                            filterMethod: EFilterMethods.IS_TRUTHY
                        }
                    }
                ]
            },
            {
                id: 'ITEMS_WITHOUT_RESPONDABLE_AND_NOT_READING_SELECTION', caption: {en: 'Items without respondables that don\'t have reading selection set to true', fr: 'Items without respondables that don\'t have reading selection set to true'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: { inverse: true }
                        }
                    },
                    {
                        checkType: AuditCheckType.ITEM_SETTINGS,
                        checkConfig: {
                            targetProp: 'isReadingSelectionPage',
                            filterMethod: EFilterMethods.IS_FALSEY
                        }
                    }
                ]
            },
            {
                id: 'ITEMS_WITH_IS_RESPONDABLE_AND_READING_SELECTION', caption: {en: 'Items with is_respondable and reading selection set to true', fr: 'Items with is_respondable and reading selection set to true'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'is_respondable',
                            filterMethod: EFilterMethods.IS_TRUTHY
                        }
                    },
                    {
                        checkType: AuditCheckType.ITEM_SETTINGS,
                        checkConfig: {
                            targetProp: 'isReadingSelectionPage',
                            filterMethod: EFilterMethods.IS_TRUTHY
                        }
                    }
                ]
            }
        ]
    },
    {
        auditType: 'DATA',
        slug: 'CHANGE_SCALE_AUDIT',
        itemScope: [AuditQuestionScope.ITEMS_SCORED],
        caption: {en: 'Change Scale Audit', fr: 'Change Scale Audit'},
        isCompleteCheck: true,
        checks: [
            {
                id: 'IS_RESPONDABLE_MISSING_CHANGE_SCALES', caption: {en: 'Items with is respondable param, that don\'t have a change scale ', fr: 'Items with is respondable param, that don\'t have a change scale '}, checkType: AuditCheckType.PROPS_CONFIG,
                checkConfig: [
                    {
                        targetProp: 'is_respondable',
                        filterMethod: EFilterMethods.IS_TRUTHY
                    },
                    {
                        targetProp: 'CMOD_Scale',
                        filterMethod: EFilterMethods.IS_NULL
                    }
                ]
            },
        ]
    },
    {
        auditType: 'ADIF',
        auditTarget: AuditTarget.MSCAT_PANELS,
        slug: 'MSCAT_PANEL_MODULE_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS],
        requiredFrameworkFlags: [TestFormConstructionMethod.MSCAT],
        caption: {en: 'MSCAT Panel Audit', fr: 'MSCAT Panel Audit'},
        isCompleteCheck: true,
        checks: [
            {
                id: 'INCORRECT_NUMBER_OF_MODULES', caption: {en: 'Incorrect number of modules', fr: 'Incorrect number of modules'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.INCORRECT_NUMBER_OF_MODULES,
                    functionParams: {
                        updateCaption: true,
                        checkId: 'INCORRECT_NUMBER_OF_MODULES',
                        auditSlug: 'MSCAT_PANEL_MODULE_AUDIT'
                    }
                }
            },
            {
                id: 'MISALIGNED_ITEM_COUNT_2_3', caption: {en: 'Misaligned item count between modules 2 and 3', fr: 'Misaligned item count between modules 2 and 3'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.MISALIGNED_ITEM_COUNT_BETWEEN_MODULES,
                    functionParams: {
                        moduleIds: [2, 3]
                    }
                }
            },
            {
                id: 'MISALIGNED_ITEM_COUNT_5_6', caption: {en: 'Misaligned item count between modules 5 and 6', fr: 'Misaligned item count between modules 5 and 6'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.MISALIGNED_ITEM_COUNT_BETWEEN_MODULES,
                    functionParams: {
                        moduleIds: [5, 6]
                    }
                }
            },
            {
                id: 'MISALIGNED_SP_SUM_2_3', caption: {en: 'Misaligned SP sum between modules 2 and 3', fr: 'Misaligned SP sum between modules 2 and 3'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.MISALIGNED_META_SUM_BETWEEN_MODULES,
                    functionParams: {
                        moduleIds: [2, 3],
                        targetParam: 'SP'
                    }
                }
            },
            {
                id: 'MISALIGNED_SP_SUM_5_6', caption: {en: 'Misaligned SP sum between modules 5 and 6', fr: 'Misaligned SP sum between modules 5 and 6'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.MISALIGNED_META_SUM_BETWEEN_MODULES,
                    functionParams: {
                        moduleIds: [5, 6],
                        targetParam: 'SP'
                    }
                }
            },
            {
                id: 'INCORRECT_NUMBER_OF_OP_ITEMS_IN_MODULE',
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.INCORRECT_NUMBER_OF_OP_ITEMS_IN_MODULE,
                    functionParams: {
                        auditSlug: 'MSCAT_PANEL_MODULE_AUDIT',
                        checkId: 'INCORRECT_NUMBER_OF_OP_ITEMS_IN_MODULE'
                    }
                }
            }
        ]

    },
    {
        auditType: 'DATA',
        auditTarget: AuditTarget.TLOFT_PANELS,
        slug: 'TLOFT_PANEL_AUDIT',
        itemScope: [AuditQuestionScope.NONE],
        requiredFrameworkFlags: [TestFormConstructionMethod.TLOFT],
        caption: {en: 'TLOFT Panel Audit', fr: 'TLOFT Panel Audit'},
        isCompleteCheck: true,
        checks: [
            {
                id: 'ENABLED_DNU_PANELS', caption: {en: 'Enabled Panels with DNU in their label', fr: 'Enabled Panels with DNU in their label'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.PANEL_NAME_INCLUDES,
                    functionParams: {
                        includeDisabled: false,
                        targetString: 'dnu'
                    }
                }
            },
        ]

    },
    {
        auditType: 'CONTENT',
        auditTarget: AuditTarget.QUESTIONS,
        slug: 'DROPDOWN_MCQ_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS],
        isCompleteCheck: true,
        caption: {en: 'Dropdown MCQ audit', fr: 'Dropdown MCQ audit'},
        checks: [
            {
                id: 'REGULAR_DROPDOWN_WITH_SPECIAL_KEYBOARD_CHAR', caption: {en: 'None custom dropdown MCQ with special character', fr: 'None custom dropdown MCQ with special character'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_TO_VAL_NUMERIC,
                    functionParams: {
                        pathToPropA: 'options',
                        val: 0,
                        targetBlock: ElementType.MCQ,
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        arrayFilters: [
                            {
                                configTarget: 'pathToPropA',
                                filterMethod: EFilterMethods.EQUAL,
                                target: 'elementType',
                                val: ElementType.TEXT
                            },
                            {
                                configTarget: 'pathToPropA',
                                filterMethod: EFilterMethods.STRING_INCLUDES_SPECIAL_CHAR,
                                target: 'content'
                            }
                        ],
                        targetBlockFilters: [
                            {
                                target: 'displayStyle',
                                val: McqDisplay.DROPDOWN,
                                filterMethod: EFilterMethods.EQUAL
                            }
                        ]
                    }
                }
            },
            {
                id: 'REGULAR_DROPDOWN_WITH_NON_TEXT_OPTION', caption: {en: 'None custom dropdown MCQ with none text option', fr: 'None custom dropdown MCQ with none text option'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_TO_VAL_NUMERIC,
                    functionParams: {
                        pathToPropA: 'options',
                        val: 0,
                        targetBlock: ElementType.MCQ,
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        arrayFilters: [
                            {
                                configTarget: 'pathToPropA',
                                filterMethod: EFilterMethods.NOT_EQUAL,
                                target: 'elementType',
                                val: ElementType.TEXT
                            }
                        ],
                        targetBlockFilters: [
                            {
                                target: 'displayStyle',
                                val: McqDisplay.DROPDOWN,
                                filterMethod: EFilterMethods.EQUAL
                            }
                        ]
                    }
                }
            },
            {
                id: 'CUSTOM_DROPDOWN_WITH_SPECIAL_KEYBOARD_CHAR', caption: {en: 'Custom dropdown MCQ with special character', fr: 'Custom dropdown MCQ with special character'}, informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_TO_VAL_NUMERIC,
                    functionParams: {
                        pathToPropA: 'options',
                        val: 0,
                        targetBlock: ElementType.MCQ,
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        arrayFilters: [
                            {
                                configTarget: 'pathToPropA',
                                filterMethod: EFilterMethods.STRING_INCLUDES_SPECIAL_CHAR,
                                target: 'content'
                            }
                        ],
                        targetBlockFilters: [
                            {
                                target: 'displayStyle',
                                val: McqDisplay.CUSTOM_DROPDOWN,
                                filterMethod: EFilterMethods.EQUAL
                            }
                        ]
                    }
                }
            },
            {
                id: 'DOUBLE_CUSTOM_DROPDOWN', caption: {en: 'Contains multiple custom dropdown MCQ which might cause dropdown overlap issues', fr: 'Contains multiple custom dropdown MCQ which might cause dropdown overlap issues'}, informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                    functionParams: {
                        targetBlocks: [ElementType.MCQ],
                        val: 1,
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        arrayFilters: [
                            {
                                target: 'displayStyle',
                                val: McqDisplay.CUSTOM_DROPDOWN,
                                filterMethod: EFilterMethods.EQUAL
                            }
                        ]
                    }
                }
            },
        ]

    },

    {
        auditType: 'CONTENT',
        auditTarget: AuditTarget.LINEAR_SECTIONS,
        slug: 'LINEAR_STRUCTURE_AUDIT',
        itemScope: [AuditQuestionScope.SCREENS],
        isCompleteCheck: true,
        requiredFrameworkFlags: [TestFormConstructionMethod.LINEAR],
        caption: {en: 'Linear Structure Audit', fr: 'Linear Structure Audit'},
        checks: [
            {
                id: 'TEST_DESIGN_WITH_NO_SECTIONS', caption: {en: 'Test design without sections', fr: 'Test design without sections'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.MISSING_LINEAR_SECTIONS,
                }
            },
            {
                id: 'TEST_DESIGN_WITH_EMPTY_SECTION', caption: {en: 'Sections without items', fr: 'Sections without items'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.EMPTY_LINEAR_SECTIONS,
                }
            },
        ]

    },
    {
        auditType: 'CONTENT',
        auditTarget: AuditTarget.QUESTIONS,
        slug: 'HIGH_CONTRAST_AUDTI',
        itemScope: [AuditQuestionScope.SCREENS, AuditQuestionScope.ITEM_BANK],
        isCompleteCheck: true,
        caption: {en: "High Contrast Audit", fr: "High Contrast Audit"},
        checks: [
            {
                id: 'SELECTION_TABLE_WITHOUT_AVOID_INVERSION', caption: {en: 'Contains selection table block(s) that don\'t have the first row as a header or isn\'t marked as hidden', fr: 'Contains selection table block(s) that don\'t have the first row as a header or isn\'t marked as hidden'},
                checkType: AuditCheckType.COMPOSITE_OR,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_TO_VAL_NUMERIC,
                            functionParams: {
                                pathToPropA: 'topRow',
                                val: 0,
                                comparisonMethod: EFilterMethods.GREATER_THAN,
                                targetBlock: ElementType.SELECT_TABLE,
                                arrayFilters: [
                                    {
                                        target: 'content.elementType',
                                        val: ElementType.TEXT,
                                        filterMethod: EFilterMethods.EQUAL
                                    },
                                    {
                                        target: 'content.caption',
                                        filterMethod: EFilterMethods.IS_NOT_NULL
                                    },
                                    {
                                        target: 'content.isNoInvertOnHiContrast',
                                        filterMethod: EFilterMethods.IS_FALSEY
                                    }

                                ],
                                targetBlockFilters: [
                                    {
                                        target: 'isFirstRowHeader',
                                        filterMethod: EFilterMethods.IS_FALSEY
                                    },
                                    {
                                        target: 'isTopRowHidden',
                                        filterMethod: EFilterMethods.IS_FALSEY
                                    }
                                ]
                            }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_TO_VAL_NUMERIC,
                            functionParams: {
                                pathToPropA: 'topRow',
                                val: 0,
                                comparisonMethod: EFilterMethods.GREATER_THAN,
                                targetBlock: ElementType.SELECT_TABLE,
                                arrayFilters: [
                                    {
                                        target: 'content.elementType',
                                        val: ElementType.MATH,
                                        filterMethod: EFilterMethods.EQUAL
                                    },
                                    {
                                        target: 'content.latex',
                                        filterMethod: EFilterMethods.IS_NOT_NULL
                                    },
                                    {
                                        target: 'content.isNoInvertOnHiContrast',
                                        filterMethod: EFilterMethods.IS_FALSEY
                                    }

                                ],
                                targetBlockFilters: [
                                    {
                                        target: 'isFirstRowHeader',
                                        filterMethod: EFilterMethods.IS_FALSEY
                                    },
                                    {
                                        target: 'isTopRowHidden',
                                        filterMethod: EFilterMethods.IS_FALSEY
                                    }
                                ]
                            }
                        }
                    }
                ]
            },
        ]
    },
    {
        auditType: 'ADIF',
        slug: 'EXPECTED_ANSWER_AUDIT',
        itemScope: [AuditQuestionScope.ITEMS_SCORED_MINUS_HS],
        caption: {en: 'Possible Expected Answer Audit', fr: 'Possible Expected Answer Audit'},
        isAsync: true,
        isCompleteCheck: true,
        autoFixes: {
            slug: 'EXPECTED_ANSWER_AUDIT_IS_AUTOFIXING',
            caption: {en: "Fix Single MCQ", fr: "Fix Single MCQ"}
        },
        checks: [
            // FROM POSSIBLE_EXPECTED_ANSWER_AUDIT
            {
                id: 'MISSING_CORRECT_ANSWER', caption: {en: 'None single MCQ items without any simulated responses', fr: 'None single MCQ items without any simulated responses'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.RESPONDABLE_COUNT,
                                    functionParams: {
                                        excludedBlocks: [ElementType.MCQ],
                                        val: 0,
                                        comparisonMethod: EFilterMethods.GREATER_THAN
                                    }
                                }
                            },
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.MCQ],
                                        val: 1,
                                        comparisonMethod: EFilterMethods.GREATER_THAN
                                    }
                                }
                            }
                        ]
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.EXPECTED_ANSWER,
                            functionParams: {
                                count: 0,
                                comparisonMethod: EFilterMethods.EQUAL,
                                arrayFilters: [
                                    {
                                        target: 'score',
                                        filterMethod: EFilterMethods.EQUAL,
                                        val: 'weight',
                                        isValPath: true
                                    }
                                ]
                            }
                        }
                    }
                ]
            },
            {
                id: 'SINGLE_MCQ_MISSING_POSSIBLE_EXPECTED_ANSWER', caption: {en: 'Single MCQ items without any simulated response', fr: 'Single MCQ items without any simulated response'}, autoFix: true,
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.COMPOSITE_OR,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.RESPONDABLE_COUNT,
                                    functionParams: {
                                        excludedBlocks: [ElementType.MCQ],
                                        val: 0,
                                        comparisonMethod: EFilterMethods.EQUAL
                                    }
                                }
                            },
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                                    functionParams: {
                                        targetBlocks: [ElementType.MCQ],
                                        val: 1,
                                        comparisonMethod: EFilterMethods.EQUAL
                                    }
                                }
                            }
                        ]
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.EXPECTED_ANSWER,
                            functionParams: {
                                count: 0,
                                comparisonMethod: EFilterMethods.EQUAL,
                                arrayFilters: [
                                    {
                                        target: 'score',
                                        filterMethod: EFilterMethods.EQUAL,
                                        val: 'weight',
                                        isValPath: true
                                    }
                                ]
                            }
                        }
                    }
                ]
            },
            {
                id: 'MISSING_POSSIBLE_EXPECTED_ANSWER', caption: {en: 'Items missing possible expected answers', fr: 'Items missing possible expected answers'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.EXPECTED_ANSWER,
                    functionParams: {
                        count: 0,
                        comparisonMethod: EFilterMethods.EQUAL
                    }
                }
            },
            {
                id: 'MISSING_ZERO_SCORE_ENTRY', caption: {en: 'Items missing a zero score entry', fr: 'Items missing a zero score entry'}, // some of these could be in expected ans audit but too cluttered already
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.EXPECTED_ANSWER,
                    functionParams: {
                        count: 0,
                        comparisonMethod: EFilterMethods.EQUAL,
                        arrayFilters: [
                            {
                                target: 'score',
                                filterMethod: EFilterMethods.EQUAL,
                                val: 0
                            }
                        ]
                    }
                }
            },
            {
                id: 'MULTI_POSSIBLE_EXPECTED_ANSWER', caption: {en: 'Items contains multiple possible expected answers', fr: 'Items contains multiple possible expected answers'}, informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.EXPECTED_ANSWER,
                    functionParams: {
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        count: 1,
                        arrayFilters: [
                            {
                                target: 'score',
                                filterMethod: EFilterMethods.EQUAL,
                                val: 'weight',
                                isValPath: true
                            }
                        ]
                    }
                }
            },
            {
                id: 'MISSING_NONE_MAX_SCORES', caption: {en: 'Items that only contain correct response', fr: 'Items that only contain correct response'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.EXPECTED_ANSWER,
                    functionParams: {
                        comparisonMethod: EFilterMethods.EQUAL,
                        count: 0,
                        arrayFilters: [
                            {
                                target: 'score',
                                filterMethod: EFilterMethods.NOT_EQUAL,
                                val: 'weight',
                                isValPath: true
                            }
                        ]
                    }
                }
            },
            {
                id: 'CONTAINS_ONE_POSSIBLE_EXPECTED_ANSWER', caption: {en: 'Items contains only one possible expected answer', fr: 'Items contains only one possible expected answer'}, informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.EXPECTED_ANSWER,
                    functionParams: {
                        comparisonMethod: EFilterMethods.EQUAL,
                        count: 1
                    }
                }
            },
            {
                id: 'COUNT_MULTI_SELECT_MCQ_WITH_POSSIBLE_MISSING_EA', caption: {en: 'Multi Select MCQ items with possibly missing expected answers for full / partial marks', fr: 'Multi Select MCQ items with possibly missing expected answers for full / partial marks'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COUNT_MULTI_SELECT_MCQ_WITH_POSSIBLE_MISSING_EA
                }
            },
            // {id: 'MISSING_CORRECT_ANSWER', caption: {en: 'MCQ Items missing the correct answer', fr: 'MCQ Items missing the correct answer'}, },
            // {id: 'SINGLE_MCQ_MISSING_POSSIBLE_CORRECT_ANSWER', caption: {en: 'Single MCQ question missing correct simulated response', fr: 'Single MCQ question missing correct simulated response'}, },
            {
                id: 'FORMATTED_RESPONSE_WITH_MULTIPLE_SCORES', caption: {en: 'Contains formatted responses with multiple scores', fr: 'Contains formatted responses with multiple scores'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.FORMATTED_RESPONSE_WITH_MULTIPLE_SCORES
                }
            },
            // FROM EXP_ANS 
            // {id:'Q_EXP_ANS_MISMATCH', caption: {en: `MCQ items configuration doesn\'t match simulated expected answer`, fr: `MCQ items configuration doesn\'t match simulated expected answer`}, excludedItems: ['QUESTIONNAIRE', 'READING_SELECTIONS'], autoFix: true},
            {
                id: 'Q_EXP_ANS_LANG_MISMATCH', caption: {en: `contain(s) different expected answers between languages`, fr: `contain(s) different expected answers between languages`}, excludedItems: ['QUESTIONNAIRE', 'READING_SELECTIONS'],
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.Q_EXP_ANS_LANG_MISMATCH
                }
            },
            {
                id: 'Q_EXP_ANS_MULTI_ENTRY', caption: {en: `contain(s) more than one response entry `, fr: `contain(s) more than one response entry `}, informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.RESPONDABLE_COUNT,
                    functionParams: {
                        val: 1,
                        comparisonMethod: EFilterMethods.GREATER_THAN
                    }
                }
            },//
            {
                id: 'Q_EXP_ANS_NO_ENTRY', caption: {en: `contain(s) no response entry `, fr: `contain(s) no response entry `}, excludedItems: ['READING_SELECTIONS'],
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.RESPONDABLE_COUNT,
                    functionParams: {
                        val: 0,
                        comparisonMethod: EFilterMethods.EQUAL
                    }
                }
            },//
            {
                id: 'Q_EXP_ANS_NOT_MCQ', caption: {en: `contain(s) none MCQ responses`, fr: `contain(s) none MCQ responses`}, excludedItems: ['READING_SELECTIONS'], informativeOnly: true,
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.RESPONDABLE_COUNT,
                    functionParams: {
                        excludedBlocks: [ElementType.MCQ],
                        val: 0,
                        comparisonMethod: EFilterMethods.GREATER_THAN
                    }
                }

            },
            { id: 'Q_EXP_ANS_NO_ACC', caption: {en: `MCQ that contain(s) no specified correct answer `, fr: `MCQ that contain(s) no specified correct answer `}, excludedItems: ['QUESTIONNAIRE', 'READING_SELECTIONS'],
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_TO_VAL_NUMERIC,
                    functionParams: {
                        val: 0,
                        comparisonMethod: EFilterMethods.EQUAL,
                        targetBlock: ElementType.MCQ,
                        pathToPropA: 'options',
                        arrayFilters: [
                            {
                                target: 'isCorrect',
                                filterMethod: EFilterMethods.IS_TRUTHY,
                                
                            }
                        ]
                    }
                }
             },
        ],
    },
    {
        auditType: 'ADIF',
        auditTarget: AuditTarget.QUESTIONS,
        slug: 'IRT_AUDIT',
        itemScope: [AuditQuestionScope.ITEMS_SCORED],
        isCompleteCheck: true,
        caption: {en: 'Item Register Audit Validation', fr: 'Item Register Audit Validation'},
        autoFixes: {
            slug: 'IRT_AUDIT_IS_AUTOFIXING',
            caption: {en: '', fr: ''},
        },
        checks: [
            {
                id: 'MISSING_SK',
                caption: {en: 'Items missing SK parameter', fr: 'Items missing SK parameter'},
                checkType: AuditCheckType.PROPS_CONFIG,
                checkConfig: [
                    { targetProp: 'SK', filterMethod: EFilterMethods.IS_NULL },
                    { targetProp: 'ItemType', filterMethod: EFilterMethods.DOES_NOT_INCLUDE, filterValue: ['LW', 'SW'] },

                ],
                // excludedItems: ['WRITING_WITHOUT_ASSOCIATED_RESOURCE']
            },
            {
                id: 'MISSING_SK',
                caption: {en: 'LW/SW items including SK parameter', fr: 'LW/SW items including SK parameter'},
                checkType: AuditCheckType.PROPS_CONFIG,
                checkConfig: [
                    { targetProp: 'SK', filterMethod: EFilterMethods.IS_TRUTHY },
                    { targetProp: 'ItemType', filterMethod: EFilterMethods.INCLUDES, filterValue: ['LW', 'SW'] },

                ],
                // excludedItems: ['WRITING_WITHOUT_ASSOCIATED_RESOURCE']
            },
            {
                id: 'MISSING_TEI',
                caption: {en: 'TEI Items missing TEI_Type parameter', fr: 'TEI Items missing TEI_Type parameter'},
                checkType: AuditCheckType.PROPS_CONFIG,
                checkConfig: [
                    { targetProp: 'TEI', filterMethod: EFilterMethods.IS_TRUTHY },
                    { targetProp: 'TEI_Type', filterMethod: EFilterMethods.IS_NULL }
                ]
            },
            {
                id: 'MISSING_NSE',
                caption: {en: 'Items missing NSE param', fr: 'Items missing NSE param'},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: { targetProp: 'NSE', filterMethod: EFilterMethods.IS_NULL }
            },
            {
                id: 'MISCONFIGURED_EXPECTATION_PARAMS',
                caption: {en: 'Items where Strand and/or NOE parameter are misaligned with NSE parameter', fr: 'Items where Strand and/or NOE parameter are misaligned with NSE parameter'},
                requiredFrameworkParamCodes: ['NSE', 'Strand', 'NOE'],
                autoFix: true,
                checkType: AuditCheckType.COMPOSITE_OR,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.Q_PARAM_MISMATCH,
                            functionParams: {
                                compositeParam: 'NSE',
                                componentParam: 'NOE',
                                indexStart: 0
                            }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.Q_PARAM_MISMATCH,
                            functionParams: {
                                compositeParam: 'NOE',
                                componentParam: 'Strand',
                                indexStart: 0
                            }
                        }
                    }
                ]
            },
            {
                id: 'MISSING_REPORTING_SUBJECT',
                caption: {en: 'Items missing reporting_subject parameter', fr: 'Items missing reporting_subject parameter'},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: { targetProp: 'reporting_subject', filterMethod: EFilterMethods.IS_NULL }
            },
            {
                id: 'MISSING_CHANGE_SCALE',
                caption: {en: 'Items missing change_scale parameter', fr: 'Items missing change_scale parameter'},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: { targetProp: 'change_scale', filterMethod: EFilterMethods.IS_NULL }
            },
            {
                id: 'MISSING_ITEM_TYPE', // duplicated in item type parameter
                caption: {en: 'Items missing ItemType parameter', fr: 'Items missing ItemType parameter'},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: { targetProp: 'ItemType', filterMethod: EFilterMethods.IS_NULL }
            },
        ]
    },
    {
        auditType: 'PSYCH',
        auditTarget: AuditTarget.QUESTIONS,
        slug: 'SCORING_CATEGORY_AUDIT',
        itemScope: [AuditQuestionScope.ITEMS_SCORED],
        caption: {en: 'Score Categories Audit', fr: 'Score Categories Audit'},
        isCompleteCheck: true,
        isAsync: true,
        checks: [
            {
                id: 'MISSING_OR_MISCONFIGURED_SCORE_CATEGORY_FIELDS', caption: {en: 'Score category primary keys and none nullable fields are invalid', fr: 'Score category primary keys and none nullable fields are invalid'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.SCORE_CATEGORIES_MISCONFIGURED_KEYS
                }
            },
            {
                id: 'MISSING_ZERO_SCORE_ENTRY', caption: {en: 'Items missing a zero score entry', fr: 'Items missing a zero score entry'}, // some of these could be in expected ans audit but too cluttered already
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {
                                excludeHumanScored: true
                            }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.EXPECTED_ANSWER,
                            functionParams: {
                                arrayFilters: [
                                    {
                                        target: 'score',
                                        filterMethod: EFilterMethods.EQUAL,
                                        val: 0
                                    }
                                ]
                            }
                        }
                    }
                ]

            },
            {
                id: 'NON_TEI_MISSING_ONE_SCORE_ENTRY', caption: {en: 'Items that don\'t have TEI param set and don\'t have a simulated response with score of 1', fr: 'Items that don\'t have TEI param set and don\'t have a simulated response with score of 1'}, excludedItems: ['ALTERNATIVE'],
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {
                                excludeHumanScored: true
                            }
                        }
                    },
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'TEI',
                            filterMethod: EFilterMethods.IS_FALSEY
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.EXPECTED_ANSWER,
                            functionParams: {
                                arrayFilters: [
                                    {
                                        target: 'score',
                                        filterMethod: EFilterMethods.EQUAL,
                                        val: 1
                                    }
                                ]
                            }
                        }
                    }
                ]
            },
            {
                id: 'MISSING_SCORE_CATEGORIES', caption: {en: 'Items that are respondable and are missing a score category ', fr: 'Items that are respondable and are missing a score category '},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.SCORE_CATEGORIES_MISSING
                }
            },
            {
                id: 'ABS_DIFFERENCE_BETWEEN_SCORE_GREATER_THAN_TWO', caption: {en: 'Items where the score category and score have an absolute difference that is greater than 2', fr: 'Items where the score category and score have an absolute difference that is greater than 2'}, excludedItems: ['ALTERNATIVE'],
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {
                                excludeHumanScored: true
                            }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.ABS_DIFFERENCE_BETWEEN_SCORE_GREATER_THAN_TWO
                        }
                    }
                ]
            },
            {
                id: 'NUMBER_OF_SCORE_CATEGORY_MATCHES_THRESHHOLD_PARAM', caption: {en: 'Items where the number of score categories and threshold params aren\'t equal', fr: 'Items where the number of score categories and threshold params aren\'t equal'}, excludedItems: ['ALTERNATIVE'],
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {
                                excludeHumanScored: true
                            }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.NUMBER_OF_SCORE_CATEGORY_MATCHES_THRESHHOLD_PARAM
                        }
                    }
                ]
            },
            {
                id: 'SCORE_CATEGORY_SEQUENTIAL_NO_MISSING_INTERMEDIATE', caption: {en: 'Items where the score categories is not sequentially ordered and are missing intermediate scores', fr: 'Items where the score categories is not sequentially ordered and are missing intermediate scores'}, excludedItems: ['ALTERNATIVE'],
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {
                                excludeHumanScored: true
                            }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.SCORE_CATEGORY_SEQUENTIAL_NO_MISSING_INTERMEDIATE
                        }
                    }
                ]
            },
            {
                id: 'EXPS_ANS_SCORE_MISSING_FROM_SCORE_CAT', caption: {en: 'Items with expected answers containing scores not in score category table', fr: 'Items with expected answers containing scores not in score category table'}, excludedItems: ['ALTERNATIVE'],
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {
                                excludeHumanScored: true
                            }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.EXPS_ANS_SCORE_MISSING_FROM_SCORE_CAT
                        }
                    }
                ]
            }
        ]
    },
    {
        auditType: 'ADIF',
        slug: 'SCORE_POINT_AUDIT',
        auditTarget: AuditTarget.QUESTIONS,
        itemScope: [AuditQuestionScope.ITEMS_SCORED_MINUS_HS], // todo: how could we include the human scored items too?
        caption: {en: 'Score Point Audit', fr: 'Score Point Audit'},
        requiredFrameworkParamCodes: ['SP'],
        isCompleteCheck: true,
        isAsync: true,
        checks: [
            { id: 'ITEMS_WITH_SCORE_POINT_COUNT', caption: {en: 'Items with SCORE POINT PARAM', fr: 'Items with SCORE POINT PARAM'}, informativeOnly: true,
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'SP',
                    filterMethod: EFilterMethods.IS_NOT_NULL
                }
            },
            { id: 'MISSING_SCORE_POINT', caption: {en: 'Items missing SCORE POINT PARAM', fr: 'Items missing SCORE POINT PARAM'},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'SP',
                    filterMethod: EFilterMethods.IS_NULL
                } },
            { id: 'INVALID_SCORE_POINT_ALIGNMENT', caption: {en: 'SCORE POINT is not aligned with total weight of the item', fr: 'SCORE POINT is not aligned with total weight of the item'}, 
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_WEIGHT_TO_PARAM,
                    functionParams: {
                        param: 'SP',
                        comparisonMethod: EFilterMethods.NOT_EQUAL
                    }
                }
            },
            { id: 'INVALID_SCORE_POINT_EXCEEDING_TOTAL_WEIGHT', caption: {en: 'SCORE POINT exceeding total weight of the item', fr: 'SCORE POINT exceeding total weight of the item'}, 
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_WEIGHT_TO_PARAM,
                    functionParams: {
                        param: 'SP',
                        comparisonMethod: EFilterMethods.GREATER_THAN
                    }
                }},
            { id: 'INVALID_SCORE_POINT_BELOW_TOTAL_WEIGHT', caption: {en: 'SCORE POINT is less then total weight of the item', fr: 'SCORE POINT is less then total weight of the item'}, 
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_WEIGHT_TO_PARAM,
                    functionParams: {
                        param: 'SP',
                        comparisonMethod: EFilterMethods.LESS_THAN
                    }
                } },
            { id: 'ITEMS_SCORE_POINT_EXCEEDS_MAX_SCORE_EA_SUBMISSIONS', caption: {en: 'Items with SCORE POINT exceeding max score of all submissions', fr: 'Items with SCORE POINT exceeding max score of all submissions'}, 
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_EA_WEIGHT_TO_PARAM,
                    functionParams: {
                        param: 'SP',
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        expectedAnsTarget: 'score',
                        aggregate: 'max'
                    }
                }

            },
            { id: 'ITEMS_SCORE_POINT_BELOW_MIN_SCORE_EA_SUBMISSIONS', caption: {en: 'Items with SCORE POINT below the min score of all submissions', fr: 'Items with SCORE POINT below the min score of all submissions'}, 
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_EA_WEIGHT_TO_PARAM,
                    functionParams: {
                        param: 'SP',
                        comparisonMethod: EFilterMethods.LESS_THAN,
                        expectedAnsTarget: 'score',
                        aggregate: 'min'
                    }
                } },
            { id: 'ITEMS_SCORE_POINT_EXCEEDS_MIN_WEIGHT_EA_SUBMISSIONS', caption: {en: 'Items with SCORE POINT exceeding min weight of all submissions', fr: 'Items with SCORE POINT exceeding min weight of all submissions'}, 
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_EA_WEIGHT_TO_PARAM,
                    functionParams: {
                        param: 'SP',
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        expectedAnsTarget: 'weight',
                        aggregate: 'min'
                    }
                } },
            { id: 'ITEMS_SCORE_POINT_BELOW_MAX_WEIGHT_EA_SUBMISSIONS', caption: {en: 'Items with SCORE POINT below the max weight of all submissions', fr: 'Items with SCORE POINT below the max weight of all submissions'}, 
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_EA_WEIGHT_TO_PARAM,
                    functionParams: {
                        param: 'SP',
                        comparisonMethod: EFilterMethods.LESS_THAN,
                        expectedAnsTarget: 'weight',
                        aggregate: 'max'
                    }
                } },
            { id: 'SCORE_POINT_BELOW_MAXIMUM_CAPTURED_SCORE_SS', caption: {en: 'Items with Score Point param below max possible score from simulated submission', fr: 'Items with Score Point param below max possible score from simulated submission'}, 
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.COMPARE_EA_WEIGHT_TO_PARAM,
                    functionParams: {
                        param: 'SP',
                        comparisonMethod: EFilterMethods.LESS_THAN,
                        expectedAnsTarget: 'score',
                        aggregate: 'max'
                    }
                } },
        ],
    },
    {
        auditType: 'ADIF',
        slug: 'ASSOCIATED_READING_PASSAGE_AUDIT',
        itemScope: [AuditQuestionScope.PASSAGE_ITEMS], // based on the check, we are not looking at the passages themselves (SCREENS_PASSAGES), but checking if they should have one
        isCompleteCheck: true,
        caption: {en: 'Associated reading passage audit', fr: 'Associated reading passage audit'},
        autoFixes: {
            slug: 'ASSOCIATED_READING_PASSAGE_AUDIT_IS_AUTOFIXING',
            caption: {en: '', fr: ''},
        },
        checks: [
            {
                id: 'MISSING_READING_PASSAGE', caption: {en: 'Items missing associated texts', fr: 'Items missing associated texts'},
                checkType: AuditCheckType.ITEM_SETTINGS,
                checkConfig: {
                    targetProp: 'readSelections',
                    filterMethod: EFilterMethods.ARRAY_LENGTH_COMPARISON_EQUAL,
                    filterValue: 0
                }
            },
            {
                id: 'MISALIGNED_READING_PASSAGE', caption: {en: 'Items associated texts doesn\'t match the items label', fr: 'Items associated texts doesn\'t match the items label'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.MISALIGNED_READING_PASSAGE
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {}
                        }
                    }
                ]
            },
            {
                id: 'INVALID_READING_PASSAGE', caption: {en: 'Items have an associated text that doesn\'t exist', fr: 'Items have an associated text that doesn\'t exist'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.INVALID_READING_PASSAGE,
                            functionParams: { excludeTrailing: true }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {}
                        }
                    }
                ]
            },
            {
                id: 'TRAILING_SPACE_IN_READING_PASSAGE', caption: {en: 'Items have an associated text with trailing spaces', fr: 'Items have an associated text with trailing spaces'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.INVALID_READING_PASSAGE,
                            functionParams: { onlyTrailing: true }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {}
                        }
                    }
                ]
            },
            {
                id: 'MISSING_BOOKMARK_ID', caption: {en: 'Items missing bookmark id', fr: 'Items missing bookmark id'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {}
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_TO_VAL_NUMERIC,
                            functionParams: {
                                pathToPropA: 'advancedList',
                                targetBlock: ElementType.TEXT,
                                val: 0,
                                comparisonMethod: EFilterMethods.GREATER_THAN,
                                arrayFilters: [
                                    {
                                        target: 'bookmarkId',
                                        filterMethod: EFilterMethods.IS_NULL
                                    },
                                    {
                                        target: 'elementType',
                                        filterMethod: EFilterMethods.EQUAL,
                                        val: ElementType.BOOKMARK_LINK
                                    }
                                ],
                                targetBlockFilters: [
                                    {
                                        target: 'paragraphStyle',
                                        filterMethod: EFilterMethods.EQUAL,
                                        val: TextParagraphStyle.ADVANCED_INLINE
                                    }
                                ]
                            }
                        }
                    }
                ]
            },
            {
                id: 'MISSING_TARGET_ITEM', caption: {en: 'Items missing target item', fr: 'Items missing target item'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {}
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.COMPARE_ELEMENT_PROPERTY_TO_VAL_NUMERIC,
                            functionParams: {
                                pathToPropA: 'advancedList',
                                targetBlock: ElementType.TEXT,
                                val: 0,
                                comparisonMethod: EFilterMethods.GREATER_THAN,
                                arrayFilters: [
                                    {
                                        target: 'itemLabel',
                                        filterMethod: EFilterMethods.IS_NULL
                                    },
                                    {
                                        target: 'elementType',
                                        filterMethod: EFilterMethods.EQUAL,
                                        val: ElementType.BOOKMARK_LINK
                                    }
                                ],
                                targetBlockFilters: [
                                    {
                                        target: 'paragraphStyle',
                                        filterMethod: EFilterMethods.EQUAL,
                                        val: TextParagraphStyle.ADVANCED_INLINE
                                    }
                                ]
                            }
                        }
                    }
                ]
            },
            {
                id: 'INVALID_TARGET_ITEM', caption: {en: 'Items with an invalid target item', fr: 'Items with an invalid target item'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.INVALID_BOOKMARK_TARGET,
                            functionParams: { excludeTrailing: true }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {}
                        }
                    }
                ]
            },
            {
                id: 'TRAILING_SPACE_IN_TARGET_ITEM', caption: {en: 'Items with a target item that has trailing spaces', fr: 'Items with a target item that has trailing spaces'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.INVALID_BOOKMARK_TARGET,
                            functionParams: { onlyTrailing: true }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.HAS_RESPONDABLE,
                            functionParams: {}
                        }
                    }
                ]
            },
            {
                id: 'INVALID_BOOKMARK_ID', caption: {en: 'Items with an invalid bookmark id', fr: 'Items with an invalid bookmark id'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.INVALID_BOOKMARK_ID
                }
            },
            {
                id: 'MISSING_PASSAGEID_PARAMETER_MULTIPLE', caption: {en: 'Items that do not contain a passageID parameter containing multiple reading selections', fr: 'Items that do not contain a passageID parameter containing multiple reading selections'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'passageID',
                            filterMethod: EFilterMethods.IS_NULL
                        }
                    },
                    {
                        checkType: AuditCheckType.ITEM_SETTINGS,
                        checkConfig: {
                            targetProp: 'readSelections',
                            filterMethod: EFilterMethods.ARRAY_LENGTH_COMPARISON_GREATER,
                            filterValue: 1

                        }
                    }
                ],
            },
            {
                id: 'MISSING_PASSAGEID_PARAMETER_SINGLE', caption: {en: 'Items that do not contain a passageID parameter containing a single reading selections', fr: 'Items that do not contain a passageID parameter containing a single reading selections'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.PROP_VAL,
                        checkConfig: {
                            targetProp: 'passageID',
                            filterMethod: EFilterMethods.IS_NULL
                        }
                    },
                    {
                        checkType: AuditCheckType.ITEM_SETTINGS,
                        checkConfig: {
                            targetProp: 'readSelections',
                            filterMethod: EFilterMethods.ARRAY_LENGTH_COMPARISON_EQUAL,
                            filterValue: 1

                        }
                    }
                ],
                autoFix: true
            },
            {
                id: 'MISSING_P_TITLE_PARAMETER', caption: {en: 'Items that do not contain a P_Title parameter', fr: 'Items that do not contain a P_Title parameter'},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'P_Title',
                    filterMethod: EFilterMethods.IS_NULL
                },
            },
            {
                id: 'MISSING_PTYP_PARAMETER', caption: {en: 'Items that do not contain a PTYP parameter', fr: 'Items that do not contain a PTYP parameter'},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'PTYP',
                    filterMethod: EFilterMethods.IS_NULL
                },
            },
        ]
    },
    {
        auditType: 'ADIF',
        slug: 'HUMAN_SCORED_AUDIT',
        itemScope: [AuditQuestionScope.ITEMS_HUMAN_SCORED],
        isCompleteCheck: true,
        caption: {en: 'Human Scored Items Audit', fr: 'Human Scored Items Audit'},
        isAsync: true,
        checks: [
            {
                id: 'MISSING_HUMAN_SCORED',
                caption: {en: 'Human Scored Items missing isHumanScored prop', fr: 'Human Scored Items missing isHumanScored prop'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.SCORING_CONFIG,
                    functionParams: {
                        target: 'is_human_scored',
                        comparisonMethod: EFilterMethods.NOT_EQUAL,
                        count: 1
                    }
                }
            },
            {
                id: 'MISSING_SCALES',
                caption: {en: 'Human Scored Items missing SCALES', fr: 'Human Scored Items missing SCALES'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.SCORING_CONFIG,
                    functionParams: {
                        target: 'scales',
                        comparisonMethod: EFilterMethods.EQUAL,
                        count: 0
                    }
                }
            },
            {
                id: 'SCALE_MISSING_PROFILE_ID',
                caption: {en: 'Human Scored Items with scale missing Score Profile ID', fr: 'Human Scored Items with scale missing Score Profile ID'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.SCORING_CONFIG,
                    functionParams: {
                        target: 'scales',
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        count: 0,
                        arrayFilters: [
                            {
                                target: 'score_profile_id',
                                filterMethod: EFilterMethods.IS_NULL
                            }
                        ]
                    }
                }
            },
            {
                id: 'MISSING_INPUT_BLOCK',
                caption: {en: 'Human Scored Items missing short answer block', fr: 'Human Scored Items missing short answer block'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.ELEMENT_COUNT,
                    functionParams: {
                        targetBlocks: [ElementType.INPUT],
                        comparisonMethod: EFilterMethods.GREATER_THAN,
                        val: 0,
                        arrayFilters: [
                            {
                                target: 'format',
                                filterMethod: EFilterMethods.EQUAL,
                                val: InputFormat.TEXT
                            }
                        ]
                    }
                }
            },
            {
                id: 'MISCONFIGURED_SCORING_SCALE',
                caption: {en: 'Human Scored Items with misconfigured scale please reset scales', fr: 'Human Scored Items with misconfigured scale please reset scales'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.MISCONFIGURED_SCORING_SCALE
                }
            },
            {
                id: 'HAS_TEI_PARAMS_SET',
                caption: {en: 'Human scored with TEI or TEI_Type set', fr: 'Human scored with TEI or TEI_Type set'},
                checkType: AuditCheckType.PROPS_CONFIG_OR,
                checkConfig: [
                    {
                        targetProp: 'TEI',
                        filterMethod: EFilterMethods.IS_TRUTHY
                    },
                    {
                        targetProp: 'TEI_Type',
                        filterMethod: EFilterMethods.IS_TRUTHY
                    }
                ]
            },
            {
                id: 'MISALIGNED_ITEM_TYPE_OR_LABEL',
                caption: {en: 'Human Scored Items with OR/SW in their label or meta, but not both', fr: 'Human Scored Items with OR/SW in their label or meta, but not both'},
                informativeOnly: true,
                checkType: AuditCheckType.COMPOSITE_OR,
                checkConfig: [
                    {
                        checkType: AuditCheckType.COMPOSITE,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.PROP_VAL,
                                checkConfig: {
                                    targetProp: 'ItemType',
                                    filterValue: 'OR',
                                    filterMethod: EFilterMethods.EQUAL
                                }
                            },
                            {
                                checkType: AuditCheckType.ITEM_SETTINGS,
                                checkConfig: {
                                    targetProp: 'label',
                                    filterMethod: EFilterMethods.TARGET_DOES_NOT_INCLUDE,
                                    filterValue: '/OR'
                                }
                            }
                        ]
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.PROP_VAL,
                                checkConfig: {
                                    targetProp: 'ItemType',
                                    filterValue: 'OR',
                                    filterMethod: EFilterMethods.NOT_EQUAL
                                }
                            },
                            {
                                checkType: AuditCheckType.ITEM_SETTINGS,
                                checkConfig: {
                                    targetProp: 'label',
                                    filterMethod: EFilterMethods.TARGET_INCLUDES,
                                    filterValue: '/OR'
                                }
                            }
                        ]
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.PROP_VAL,
                                checkConfig: {
                                    targetProp: 'ItemType',
                                    filterValue: 'SW',
                                    filterMethod: EFilterMethods.EQUAL
                                }
                            },
                            {
                                checkType: AuditCheckType.ITEM_SETTINGS,
                                checkConfig: {
                                    targetProp: 'label',
                                    filterMethod: EFilterMethods.TARGET_DOES_NOT_INCLUDE,
                                    filterValue: '/SW'
                                }
                            }
                        ]
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.PROP_VAL,
                                checkConfig: {
                                    targetProp: 'ItemType',
                                    filterValue: 'SW',
                                    filterMethod: EFilterMethods.NOT_EQUAL
                                }
                            },
                            {
                                checkType: AuditCheckType.ITEM_SETTINGS,
                                checkConfig: {
                                    targetProp: 'label',
                                    filterMethod: EFilterMethods.TARGET_INCLUDES,
                                    filterValue: '/SW'
                                }
                            }
                        ]
                    }
                ]
            },
            {
                id: 'MISALIGNED_IRT_SCALE_CODE',
                caption: {en: 'Human Scored Items that are have OR/SW in their item type but scoring config is not aligned with it', fr: 'Human Scored Items that are have OR/SW in their item type but scoring config is not aligned with it'},
                checkType: AuditCheckType.COMPOSITE_OR,
                checkConfig: [
                    {
                        checkType: AuditCheckType.COMPOSITE,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.PROP_VAL,
                                checkConfig: {
                                    targetProp: 'ItemType',
                                    filterMethod: EFilterMethods.EQUAL,
                                    filterValue: 'OR'
                                }
                            },
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.MISALIGNED_IRT_SCALE_CODE,
                                    functionParams: {
                                        validScales: ['R']
                                    }
                                }
                            }
                        ]
                    },
                    {
                        checkType: AuditCheckType.COMPOSITE,
                        checkConfig: [
                            {
                                checkType: AuditCheckType.PROP_VAL,
                                checkConfig: {
                                    targetProp: 'ItemType',
                                    filterMethod: EFilterMethods.EQUAL,
                                    filterValue: 'SW'
                                }
                            },
                            {
                                checkType: AuditCheckType.BY_FUNCTION,
                                checkConfig: {
                                    functionId: ECheckFunctionIds.MISALIGNED_IRT_SCALE_CODE,
                                    functionParams: {
                                        validScales: ['T', 'V']
                                    }
                                }
                            }
                        ]
                    }
                ]
            },
        ],
    },
    {
        auditType: 'PSYCH',
        slug: 'IRT_PARAMETERS_AUDIT',
        caption: {en: 'IRT Parameters Audit', fr: 'IRT Parameters Audit'},
        itemScope: [AuditQuestionScope.ITEMS_SCORED],
        isCompleteCheck: true,
        commonCheckProps: {
            checkType: AuditCheckType.PROPS_CONFIG_PSYCH_STATS
        },
        checks: [
            {
                id: 'ALT_ITEMS_WITH_PSYCH_STATS',
                caption: {en: 'Alt items with psych stats populated', fr: 'Alt items with psych stats populated'},
                checkType: AuditCheckType.PROPS_CONFIG_PSYCH_STATS_OR,
                checkConfig: [
                    {
                        targetProp: 'a',
                        filterMethod: EFilterMethods.IS_TRUTHY
                    },
                    {
                        targetProp: 'b',
                        filterMethod: EFilterMethods.IS_TRUTHY
                    }
                ],
                excludedItems: ['NON_ALTERNATIVE']
            },
            {
                id: 'ALT_ITEMS_WITHOUT_PSYCH_STATS',
                caption: {en: 'Alt items with psych stats missing', fr: 'Alt items with psych stats missing'},
                checkType: AuditCheckType.PROPS_CONFIG_PSYCH_STATS_OR,
                checkConfig: [
                    {
                        targetProp: 'a',
                        filterMethod: EFilterMethods.IS_NULL
                    },
                    {
                        targetProp: 'b',
                        filterMethod: EFilterMethods.IS_NULL
                    }
                ],
                excludedItems: ['NON_ALTERNATIVE']
            },
            {
                id: 'CALIB_YEAR_MISSING',
                caption: {en: 'None alternative items missing calib year', fr: 'None alternative items missing calib year'},
                checkConfig: [
                    {
                        targetProp: 'calib_year',
                        filterMethod: EFilterMethods.IS_NULL
                    }
                ],
                excludedItems: ['ALTERNATIVE', 'FT']
            },
            {
                id: 'REQUIRED_ITEMS_MISSING_IRT_A',
                caption: {en: 'None FT/ALT Items missing IRT A-parameters', fr: 'None FT/ALT Items missing IRT A-parameters'},
                checkConfig: [
                    {
                        targetProp: 'a',
                        filterMethod: EFilterMethods.IS_NAN
                    }
                ],
                excludedItems: ['FT', 'ALTERNATIVE']
            },
            {
                id: 'REQUIRED_ITEMS_MISSING_IRT_B',
                caption: {en: 'None FT/ALT Items missing IRT B-parameters', fr: 'None FT/ALT Items missing IRT B-parameters'},
                checkConfig: [
                    {
                        targetProp: 'b',
                        filterMethod: EFilterMethods.IS_NAN
                    }
                ],
                excludedItems: ['FT', 'ALTERNATIVE']
            },
            {
                id: 'FT_REQUIRED_ITEMS_MISSING_IRT_A',
                caption: {en: 'FT Items have IRT A-parameters', fr: 'FT Items have IRT A-parameters'},
                checkConfig: [
                    {
                        targetProp: 'a',
                        filterMethod: EFilterMethods.IS_TRUTHY
                    }
                ],
                excludedItems: ['NON_FT']
            },
            {
                id: 'FT_REQUIRED_ITEMS_MISSING_IRT_B',
                caption: {en: 'FT Items have IRT B-parameters', fr: 'FT Items have IRT B-parameters'},
                checkConfig: [
                    {
                        targetProp: 'b',
                        filterMethod: EFilterMethods.IS_TRUTHY
                    }
                ],
                excludedItems: ['NON_FT']
            },
            {
                id: 'CHANGE_SCALE_0_MISSING_IRT_A',
                caption: {en: 'Items with `Change Scale` 0 that are missing IRT A parameters', fr: 'Items with `Change Scale` 0 that are missing IRT A parameters'},
                checkConfig: [
                    {
                        targetProp: 'change_scale',
                        filterMethod: EFilterMethods.EQUAL,
                        filterValue: 0
                    },
                    {
                        targetProp: 'a',
                        filterMethod: EFilterMethods.IS_NAN
                    }
                ]
            },
            {
                id: 'CHANGE_SCALE_0_MISSING_IRT_B',
                caption: {en: 'Items with `Change Scale` 0 that are missing IRT B parameters', fr: 'Items with `Change Scale` 0 that are missing IRT B parameters'},
                checkConfig: [
                    {
                        targetProp: 'change_scale',
                        filterMethod: EFilterMethods.EQUAL,
                        filterValue: 0
                    },
                    {
                        targetProp: 'b',
                        filterMethod: EFilterMethods.IS_NAN
                    }
                ]
            },
            {
                id: 'CHANGE_SCALE_3_WITH_IRT_A',
                caption: {en: 'Items with `Change Scale` 3 that have IRT A parameters', fr: 'Items with `Change Scale` 3 that have IRT A parameters'},
                checkConfig: [
                    {
                        targetProp: 'change_scale',
                        filterMethod: EFilterMethods.EQUAL,
                        filterValue: 3
                    },
                    {
                        targetProp: 'a',
                        filterMethod: EFilterMethods.IS_TRUTHY
                    }
                ]
            },
            {
                id: 'CHANGE_SCALE_3_WITH_IRT_B',
                caption: {en: 'Items with `Change Scale` 3 that have IRT A parameters', fr: 'Items with `Change Scale` 3 that have IRT A parameters'},
                checkConfig: [
                    {
                        targetProp: 'change_scale',
                        filterMethod: EFilterMethods.EQUAL,
                        filterValue: 3
                    },
                    {
                        targetProp: 'b',
                        filterMethod: EFilterMethods.IS_TRUTHY
                    }
                ]
            },
            {
                id: 'CHANGE_SCALE_4_WITH_IRT_A',
                caption: {en: 'Items with `Change Scale` 4  that have IRT A parameters', fr: 'Items with `Change Scale` 4  that have IRT A parameters'},
                checkConfig: [
                    {
                        targetProp: 'change_scale',
                        filterMethod: EFilterMethods.EQUAL,
                        filterValue: 4
                    },
                    {
                        targetProp: 'a',
                        filterMethod: EFilterMethods.IS_TRUTHY,
                    }
                ]
            },
            {
                id: 'CHANGE_SCALE_4_WITH_IRT_B',
                caption: {en: 'Items with `Change Scale` 4  that have IRT B parameters', fr: 'Items with `Change Scale` 4  that have IRT B parameters'},
                checkConfig: [
                    {
                        targetProp: 'change_scale',
                        filterMethod: EFilterMethods.EQUAL,
                        filterValue: 4
                    },
                    {
                        targetProp: 'b',
                        filterMethod: EFilterMethods.IS_TRUTHY
                    }
                ]
            },
            {
                id: 'NO_CALIB_YEAR_WITH_IRT_A',
                caption: {en: 'Items with IRT A parameters that do not have a calibration year set', fr: 'Items with IRT A parameters that do not have a calibration year set'},
                checkConfig: [
                    {
                        targetProp: 'calib_year',
                        filterMethod: EFilterMethods.EQUAL,
                        filterValue: 'NOPAR'
                    },
                    {
                        targetProp: 'a',
                        filterMethod: EFilterMethods.IS_NOT_NULL
                    }
                ]
            },
            {
                id: 'NO_CALIB_YEAR_WITH_IRT_B',
                caption: {en: 'Items with IRT B parameters that do not have a calibration year set', fr: 'Items with IRT B parameters that do not have a calibration year set'},
                checkConfig: [
                    {
                        targetProp: 'calib_year',
                        filterMethod: EFilterMethods.EQUAL,
                        filterValue: 'NOPAR'
                    },
                    {
                        targetProp: 'b',
                        filterMethod: EFilterMethods.IS_NOT_NULL
                    }
                ]
            },
            {
                id: 'FT_ITEMS_WITH_CALIB_YEAR',
                caption: {en: 'FT items that have a calibration year set', fr: 'FT items that have a calibration year set'},
                checkConfig: [
                    {
                        targetProp: 'calib_year',
                        filterMethod: EFilterMethods.NOT_EQUAL,
                        filterValue: 'NOPAR'
                    }
                ],
                excludedItems: ['NON_FT']
            },
            {
                id: 'OP_ITEMS_MISSING_CALIB_YEAR',
                caption: {en: 'OP items that don\'t have a calibration year set', fr: 'OP items that don\'t have a calibration year set'},
                checkConfig: [
                    {
                        targetProp: 'calib_year',
                        filterMethod: EFilterMethods.IS_NULL
                    }
                ],
                excludedItems: ['FT']
            },
            {
                id: 'PARAM_WITH_WRONG_DECIMALS',
                caption: {en: 'Psych item stat not rounded to the correct decimal point (5)', fr: 'Psych item stat not rounded to the correct decimal point (5)'},
                checkType: AuditCheckType.PROPS_CONFIG_PSYCH_STATS_OR,
                checkConfig: [
                    {
                        targetProp: 'a',
                        filterMethod: EFilterMethods.DECIMAL_ROUNDING,
                        filterValue: 5
                    },
                    {
                        targetProp: 'b',
                        filterMethod: EFilterMethods.DECIMAL_ROUNDING,
                        filterValue: 5
                    },
                    {
                        targetProp: 'b1',
                        filterMethod: EFilterMethods.DECIMAL_ROUNDING,
                        filterValue: 5
                    },
                    {
                        targetProp: 'b2',
                        filterMethod: EFilterMethods.DECIMAL_ROUNDING,
                        filterValue: 5
                    },
                    {
                        targetProp: 'b3',
                        filterMethod: EFilterMethods.DECIMAL_ROUNDING,
                        filterValue: 5
                    },
                    {
                        targetProp: 'b4',
                        filterMethod: EFilterMethods.DECIMAL_ROUNDING,
                        filterValue: 5
                    },
                    {
                        targetProp: 'b5',
                        filterMethod: EFilterMethods.DECIMAL_ROUNDING,
                        filterValue: 5
                    },
                    {
                        targetProp: 'b6',
                        filterMethod: EFilterMethods.DECIMAL_ROUNDING,
                        filterValue: 5
                    },
                    {
                        targetProp: 'g',
                        filterMethod: EFilterMethods.DECIMAL_ROUNDING,
                        filterValue: 5
                    },
                ]
            },
            {
                id: 'MISSING_B1_PARAM',
                caption: {en: 'None Ft/ALT where SP != 1 and are missing b1 param', fr: 'None Ft/ALT where SP != 1 and are missing b1 param'},
                checkConfig: [
                    {
                        targetProp: 'b1',
                        filterMethod: EFilterMethods.IS_NAN
                    },
                    {
                        targetProp: 'SP',
                        filterMethod: EFilterMethods.NOT_EQUAL,
                        filterValue: 1
                    }
                ],
                excludedItems: ['ALTERNATIVE', 'FT']
            },
            {
                id: 'MISSING_B2_PARAM',
                caption: {en: 'None Ft/ALT where SP != 1 and are missing b2 param', fr: 'None Ft/ALT where SP != 1 and are missing b2 param'},
                checkConfig: [
                    {
                        targetProp: 'b2',
                        filterMethod: EFilterMethods.IS_NAN
                    },
                    {
                        targetProp: 'SP',
                        filterMethod: EFilterMethods.NOT_EQUAL,
                        filterValue: 1
                    }
                ],
                excludedItems: ['ALTERNATIVE', 'FT']
            },
            {
                id: 'MISSING_B3_PARAM',
                caption: {en: 'None Ft/ALT where SP != 1 and are missing b3 param with b4 populated', fr: 'None Ft/ALT where SP != 1 and are missing b3 param with b4 populated'},
                checkConfig: [
                    {
                        targetProp: 'b3',
                        filterMethod: EFilterMethods.IS_NAN
                    },
                    {
                        targetProp: 'SP',
                        filterMethod: EFilterMethods.NOT_EQUAL,
                        filterValue: 1
                    },
                    {
                        targetProp: 'b4',
                        filterMethod: EFilterMethods.IS_NOT_NULL,
                    }
                ],
                excludedItems: ['ALTERNATIVE', 'FT']
            },
            {
                id: 'MISSING_B4_PARAM',
                caption: {en: 'None Ft/ALT where SP != 1 and are missing b4 param with b5 populated', fr: 'None Ft/ALT where SP != 1 and are missing b4 param with b5 populated'},
                checkConfig: [
                    {
                        targetProp: 'b4',
                        filterMethod: EFilterMethods.IS_NAN
                    },
                    {
                        targetProp: 'SP',
                        filterMethod: EFilterMethods.NOT_EQUAL,
                        filterValue: 1
                    },
                    {
                        targetProp: 'b5',
                        filterMethod: EFilterMethods.IS_NOT_NULL,
                    }
                ],
                excludedItems: ['ALTERNATIVE', 'FT']
            },
            {
                id: 'MISSING_B5_PARAM',
                caption: {en: 'None Ft/ALT where SP != 1 and are missing b5 param with b6 populated', fr: 'None Ft/ALT where SP != 1 and are missing b5 param with b6 populated'},
                checkConfig: [
                    {
                        targetProp: 'b5',
                        filterMethod: EFilterMethods.IS_NAN
                    },
                    {
                        targetProp: 'SP',
                        filterMethod: EFilterMethods.NOT_EQUAL,
                        filterValue: 1
                    },
                    {
                        targetProp: 'b6',
                        filterMethod: EFilterMethods.IS_NOT_NULL,
                    }
                ],
                excludedItems: ['ALTERNATIVE', 'FT']
            },
            {
                id: 'MISSING_B6_PARAM',
                caption: {en: 'None Ft/ALT where SP != 1 and are missing b6 param with b5 populated', fr: 'None Ft/ALT where SP != 1 and are missing b6 param with b5 populated'},
                checkConfig: [
                    {
                        targetProp: 'b6',
                        filterMethod: EFilterMethods.IS_NAN
                    },
                    {
                        targetProp: 'SP',
                        filterMethod: EFilterMethods.NOT_EQUAL,
                        filterValue: 1
                    },
                    {
                        targetProp: 'b5',
                        filterMethod: EFilterMethods.IS_NOT_NULL,
                    }
                ],
                excludedItems: ['ALTERNATIVE', 'FT']
            },
            {
                id: 'MISSING_G_PARAM',
                caption: {en: 'None Ft/ALT where SP = 1, TEI = 0 and is missing g param', fr: 'None Ft/ALT where SP = 1, TEI = 0 and is missing g param'},
                checkConfig: [
                    {
                        targetProp: 'g',
                        filterMethod: EFilterMethods.IS_NAN
                    },
                    {
                        targetProp: 'SP',
                        filterMethod: EFilterMethods.EQUAL,
                        filterValue: 1
                    },
                    {
                        targetProp: 'TEI',
                        filterMethod: EFilterMethods.IS_FALSEY,
                    }
                ],
                excludedItems: ['ALTERNATIVE', 'FT']
            }
        ]
    },
    {
        auditType: 'ADIF',
        slug: 'ALTERNATIVE_ITEM_ADIF_AUDIT',
        itemScope: [AuditQuestionScope.ITEMS_SCORED],
        caption: {en: 'Alternative Linear test Audit', fr: 'Alternative Linear test Audit'},
        isCompleteCheck: true,
        requiredFrameworkFlags: [AuditAssesmentTypeFlags.ALTERNATIVE],
        autoFixes: {
            slug: 'ALTERNATIVE_ITEM_ADIF_AUDIT_IS_AUTOFIXING',
            caption: {en: 'AUTOFIX_ALT_PARAM', fr: 'AUTOFIX_ALT_PARAM'}
        },
        checks: [
            {
                id: 'ITEMS_MISSING_REGULAR_QUESTION_ID_CHECKED',
                caption: {en: 'Items For Alternative linear test missing question_id_reg param', fr: 'Items For Alternative linear test missing question_id_reg param'},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'question_id_reg',
                    filterMethod: EFilterMethods.IS_NULL,
                }
            },
            {
                id: 'ALTERNATIVE_WITH_INVALID_QUESTION_ID_REG',
                caption: {en: 'Alternative items with invalid question ID', fr: 'Alternative items with invalid question ID'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.Q_PARAM_LINKS_TO_MISSING_QUESTION,
                    functionParams: {
                        param: 'question_id_reg'
                    }
                } 
            },
            {
                id: 'ITEMS_MISSING_ALT',
                caption: {en: 'Items For Alternative linear test missing ALT param', fr: 'Items For Alternative linear test missing ALT param'},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'ALT',
                    filterMethod: EFilterMethods.IS_NULL,
                },
                autoFix: true
            },
            {
                id: 'ITEMS_MISSING_REGULAR_QUESTION_LABEL_CHECKED',
                caption: {en: 'Items For Alternative linear test missing question_label_reg param', fr: 'Items For Alternative linear test missing question_label_reg param'},
                checkType: AuditCheckType.PROP_VAL,
                checkConfig: {
                    targetProp: 'question_label_reg',
                    filterMethod: EFilterMethods.IS_NULL,
                }
            },
            {
                id: 'ITEMS_WITH_MISALIGNED_STATS',
                caption: {en: 'Items included in the alternative linear test that have statistical misalignment with their counterparts in the regular test.', fr: 'Items included in the alternative linear test that have statistical misalignment with their counterparts in the regular test.'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.Q_PSYCH_STATS_MISALIGNED_BETWEEN_LINKED_QUESTIONS,
                    functionParams: {
                        param: 'question_label_reg'
                    }
                }
            },


        ],
    },
    {
        auditType: 'ADIF',
        slug: 'CROSS_PANEL_AUDITS',
        auditTarget: AuditTarget.PANELS,
        itemScope: [AuditQuestionScope.SCREENS],
        isCompleteCheck: true,
        caption: {en: 'Cross Panel Validations', fr: 'Cross Panel Validations'},
        checks: [
            {
                id: 'CORRECT_NUMBER_OF_OP_ITEMS',
                caption: {en: 'Panel without the correct number of operational items', fr: 'Panel without the correct number of operational items'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.CORRECT_NUMBER_OF_OP_ITEMS,
                    functionParams: {
                        auditSlug: 'CROSS_PANEL_AUDITS',
                        checkId: 'CORRECT_NUMBER_OF_OP_ITEMS'
                    }
                }
            }
        ]
    },
    {
        auditType: 'ADIF',
        slug: 'PANEL_MAPPING_AUDIT',
        auditTarget: AuditTarget.PANELS,
        itemScope: [AuditQuestionScope.SCREENS],
        isCompleteCheck: true,
        caption: {en: 'Panel Mapping Audit', fr: 'Panel Mapping Audit'},
        checks: [
            {
                id: 'PANEL_HAS_REPEATED_QUESTION',
                caption: {en: 'Panel with repeated items in them', fr: 'Panel with repeated items in them'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.PANEL_HAS_REPEATED_QUESTION
                }
            },
            {
                id: 'PANEL_HAS_INCORRECT_NUM_SECTION',
                caption: {en: 'Panel with incorrect number of sections in them', fr: 'Panel with incorrect number of sections in them'},
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.PANEL_HAS_INCORRECT_NUM_SECTION,
                    functionParams: {
                        checkId: 'PANEL_HAS_INCORRECT_NUM_SECTION',
                        auditSlug: 'PANEL_MAPPING_AUDIT'
                    }
                }
            },
            {
                id: 'INCORRECT_NUM_OF_UNIQUE_PANELS',
                caption: {en: 'Framework has incorrect number of enabled panels', fr: 'Framework has incorrect number of enabled panels'},
                excludedFrameworkFlags: [TestFormConstructionMethod.LINEAR],
                checkType: AuditCheckType.BY_FUNCTION,
                checkConfig: {
                    functionId: ECheckFunctionIds.INCORRECT_NUM_OF_UNIQUE_PANELS,
                    functionParams: {
                        checkId: 'INCORRECT_NUM_OF_UNIQUE_PANELS',
                        auditSlug: 'PANEL_MAPPING_AUDIT'
                    }
                }

            },
        ]
    },
    {
        auditType: 'DATA',
        slug: 'VALIDATOR_BLOCK_SCORE_WEIGHT_AUDIT',
        isCompleteCheck: true,
        itemScope: [AuditQuestionScope.SCREENS],
        caption: {en: 'Validator Block Score Weight Audit', fr: 'Validator Block Score Weight Audit'},
        checks: [
            {
                id: 'ITEMS_WITH_VALIDATOR_AND_NON_ZERO_WEIGHT',
                caption: {en: 'Items with a validator block and score weight not set to 0 for all of its respondables', fr: 'Items with a validator block and score weight not set to 0 for all of its respondables'},
                checkType: AuditCheckType.COMPOSITE,
                checkConfig: [
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.ELEMENT_COUNT,
                            functionParams: {
                                targetBlocks: [ElementType.VALIDATOR]
                            }
                        }
                    },
                    {
                        checkType: AuditCheckType.BY_FUNCTION,
                        checkConfig: {
                            functionId: ECheckFunctionIds.RESPONDABLE_COUNT,
                            functionParams: {
                                val: 0,
                                comparisonMethod: EFilterMethods.GREATER_THAN,
                                arrayFilters: [
                                    {   
                                        target: 'scoreWeight',
                                        filterMethod: EFilterMethods.IS_TRUTHY
                                    }
                                ]
                            }
                        }
                    }
                ]
            },
        ]
    },
]
